import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import useAuth from 'hooks/useAuth';

// material-ui
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { differenceInCalendarDays } from 'date-fns';

// ant design icons
import AimOutlined from '@ant-design/icons/AimOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import DownOutlined from '@ant-design/icons/DownOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import FolderOpenOutlined from '@ant-design/icons/FolderOpenOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import UpOutlined from '@ant-design/icons/UpOutlined';

// project imports
import MainCard from 'components/MainCard';
// `skillDomains` is still imported for the domain-grouping order (frontend-only
// concern). The hardcoded `skillsData` JS array has been retired in favour of
// the relational clinical.skill table — see services/api/skills.js and the
// seed_skills_from_js.py one-shot migration.
import { skillDomains } from 'api/skillsRepository';
import { listSkills, getSkillHierarchy } from 'services/api/skills';
import {
  listClientIepVersions,
  getIepVersion,
  // Relational endpoints — the Goals & Targets tab now reads from and writes
  // to clinical.iep_goal / clinical.iep_target instead of the older
  // iep_version.targets_overlay JSONB column. The overlay endpoints
  // (getIepVersionTargets, replaceIepVersionGoalTargets) are still alive on
  // the backend for callers we haven't migrated yet, but this file no longer
  // touches them.
  listGoals,
  createGoal,
  updateGoal,
  deleteGoal,
  listTargets,
  createTarget,
  updateTarget,
  deleteTarget,
  updateIepVersion,
  getChildMasteryRules
} from 'services/api/iep';
import GoalBuilderModal from 'sections/shared/skill-repo/GoalBuilderModal';
import GoalDetailsModal from 'sections/shared/skill-repo/GoalDetailsModal';
import TargetPickerModal from 'sections/shared/skill-repo/TargetPickerModal';
import TargetStatusHistoryButton from 'sections/shared/child-detail/TargetStatusHistory';
import { parseLadder, weaveStatement, matchStatement } from 'sections/shared/skill-repo/paramEngine';
import { selectActiveVersion } from 'services/clinicalBundle';
import { listCentralFileMaterials, listChildFileMaterials, getMaterialsMetadata } from 'services/api/materials';
import usePageAccess from 'hooks/usePageAccess';
import { activityLines } from 'utils/activityList';
import { GOAL_SERVICES, serviceHasActivities, serviceLabel } from 'utils/goalServices';
// Shared controlling-prompt code list — same source the session Cold Probe /
// Multi-Trial sheets use, so the target's Default Controlling Prompts stay in sync.
import { PROMPT_LEVELS } from 'utils/promptLevels';

// Synthetic group for goals no service has been chosen for yet. A sentinel
// rather than '' so it can be an ordering key like any real service code.
const UNASSIGNED_SERVICE_ID = '__no_service__';

// Build the absolute URL for a material image. The backend returns a
// path like `/api/v1/materials/files/...` which is already relative to
// the API host; just hand it back as-is.
const materialThumbnailUrl = (m) => m?.url || '';

// Resolve a material's display name. Central materials carry a custom name
// in the metadata JSON (managed by the supervisor's Material Repository);
// child materials store the name inside the filename itself. In both cases
// we want the saved name on the target to be human-readable ("Daddy"), not
// the raw image filename ("father.jpg").
const resolveMaterialName = (file, metadata = {}) => {
  const meta = metadata[file.path] || metadata[file.name] || {};
  if (meta.name) return meta.name;
  const nameWithoutExt = String(file.name || '').replace(/\.[^/.]+$/, '');
  return nameWithoutExt.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const TARGET_STATUS_VALUES = ['not_started', 'in_progress', 'on_hold', 'mastered'];
const TRACKING_STATUS_VALUES = ['not_introduced', 'daily_tracking', 'weekly_maintenance', 'monthly', 'long_term'];

export const TARGET_STATUS_INTL_KEYS = {
  not_started: 'goalsTargets.targetStatus.notStarted',
  in_progress: 'goalsTargets.status.inProgress',
  on_hold: 'goalsTargets.status.onHold',
  mastered: 'goalsTargets.status.mastered'
};

const TRACKING_STATUS_INTL_KEYS = {
  not_introduced: 'goalsTargets.trackingStatus.notIntroduced',
  daily_tracking: 'goalsTargets.trackingStatus.dailyTracking',
  weekly_maintenance: 'goalsTargets.trackingStatus.weeklyMaintenance',
  monthly: 'goalsTargets.trackingStatus.monthly',
  long_term: 'goalsTargets.trackingStatus.longTerm'
};

// Map a goal/target status code (e.g. "in_progress", "achieved") to its intl key.
// Falls back to prettifying the raw code (underscores → spaces).
const GOAL_STATUS_INTL_KEYS = {
  in_progress: 'goalsTargets.status.inProgress',
  completed: 'goalsTargets.status.completed',
  achieved: 'goalsTargets.status.completed',
  on_hold: 'goalsTargets.status.onHold',
  baseline: 'goalsTargets.status.baseline',
  acquisition: 'goalsTargets.status.acquisition',
  mastered: 'goalsTargets.status.mastered',
  active: 'goalsTargets.active',
  archived: 'goalsTargets.archived'
};

// Tracking phases that mean the target has passed acquisition (entered
// maintenance). Mirrors MAINTENANCE_PHASES in the backend mastery rule engine —
// reaching one of these is what makes a target "mastered" with an achieved date.
const MASTERY_TRACKING_PHASES = ['weekly_maintenance', 'monthly', 'long_term'];

// Derived — first entry in the maintenance sequence (the phase a target enters
// immediately after acquisition mastery). Avoids hardcoding the string so adding
// a new phase to MASTERY_TRACKING_PHASES above automatically applies here too.
const FIRST_MAINTENANCE_PHASE = MASTERY_TRACKING_PHASES[0];

// Derived — the active acquisition phase a target reverts to when mastery is
// removed: the last TRACKING_STATUS_VALUES entry that is neither maintenance
// nor "not_introduced" (i.e. daily_tracking, or whatever replaces it).
const ACTIVE_ACQUISITION_PHASE =
  TRACKING_STATUS_VALUES.filter((v) => !MASTERY_TRACKING_PHASES.includes(v) && v !== 'not_introduced').slice(-1)[0] ??
  TRACKING_STATUS_VALUES[1];

// Goal status written to snapshot (iepGoals[].status) when all targets master.
// Must stay in sync with IEP_GOAL_STATUS_OPTIONS in iep.jsx.
const GOAL_SNAPSHOT_STATUS_COMPLETED = 'completed';
// DB ref_goal_status.code for the same terminal state (clinical schema seed).
const GOAL_DB_STATUS_ACHIEVED = 'achieved';

const UNASSIGNED_DOMAIN_ID = '__unassigned__';

const domainLabel = (id, intl) => {
  if (!id || id === UNASSIGNED_DOMAIN_ID) {
    return intl.formatMessage({ id: 'iep.goalDomainUnassigned' });
  }
  const hit = skillDomains.find((d) => d.id === id);
  return hit ? hit.name : id;
};

// Generate a target id that's unique enough for the demo. Avoids a crypto
// dependency; collision space is enormous for typical usage.
const makeTargetId = () => `T-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const blankTarget = () => ({
  id: '',
  name: '',
  // Skill (single-select from the Skills Repository, optionally filtered
  // by the parent goal's domain). Stored as the skill's `id` (e.g.
  // `SK-MAND-001`). The skill's display name lives in the repo so we don't
  // duplicate it onto the target row.
  skillId: '',
  // Materials (multi-select from central + child-specific file repos).
  // Each item: { source: 'central'|'child', path, name, url, category? }.
  materials: [],
  parameters: [],
  nameTemplate: '',
  description: '',
  dateIntroduced: '',
  dateMastered: '',
  location: '',
  people: '',
  stimuli: '',
  status: 'in_progress',
  // Tracking phase (see TRACKING_STATUS_OPTIONS) — defaults to "Not Introduced".
  trackingStatus: 'not_introduced',
  // Default controlling prompts — array of PROMPT_LEVELS codes (e.g. ["PVP","IND"]).
  // The first is pre-selected in the session Cold Probe / Multi-Trial sheets.
  defaultControllingPrompts: [],
  // Target-specific mastery prompt level ('' = follow child / global) and whether a
  // mastery-rule change may re-compute this target. Stored only for now.
  masteryPromptLevel: '',
  excludeFromRecompute: false,
  // SD (discriminative stimulus) — free-text, multi-line, optional. Surfaced as a
  // tooltip on the session data-collection sheets so the therapist can see the
  // exact instruction/cue to deliver for this target.
  sdInstruction: '',
  // Approval. Supervisor-added targets are approved on save (the parent
  // pre-fills these). Therapist-added targets leave them blank → "pending",
  // until a supervisor approves the target in the Goals & Targets tab.
  approvedById: '',
  approvedByName: '',
  approvedDate: '',
  // Decline — set when a supervisor declines a pending target (kept, struck-through).
  declinedById: '',
  declinedByName: '',
  declinedDate: ''
});

// Days = difference between dateIntroduced and dateMastered (or today if
// not yet mastered). Returns null if dateIntroduced is missing.
export function computeDays(t) {
  if (!t?.dateIntroduced) return null;
  const start = new Date(t.dateIntroduced);
  if (Number.isNaN(start.getTime())) return null;
  const end = t.dateMastered ? new Date(t.dateMastered) : new Date();
  if (Number.isNaN(end.getTime())) return null;
  return Math.max(0, differenceInCalendarDays(end, start));
}

// The Target Library, fetched once per page and shared. The Edit dialog needs it
// the instant it opens — awaiting the request there made the Parameters block
// pop in a beat after the dialog had already drawn — so the tab warms this on
// mount and the dialog reads it synchronously when it is already there.
let skillHierarchyCache = null;
let skillHierarchyPromise = null;

const loadSkillHierarchy = () => {
  if (skillHierarchyCache) return Promise.resolve(skillHierarchyCache);
  if (!skillHierarchyPromise) {
    skillHierarchyPromise = getSkillHierarchy()
      .then((tree) => {
        skillHierarchyCache = Array.isArray(tree) ? tree : [];
        return skillHierarchyCache;
      })
      .catch((err) => {
        // Let the next caller retry rather than caching the failure.
        skillHierarchyPromise = null;
        throw err;
      });
  }
  return skillHierarchyPromise;
};

// Every library target in a domain (its own plus its target sub-domains) — the
// same set the Target Picker offers when a target is first added.
const domainLibraryTargets = (hierarchy, domainId) => {
  // Snapshot goals carry "DOMAIN-MAND"; repository codes are bare ("MAND").
  const code = String(domainId || '').replace(/^DOMAIN-/i, '');
  const dom = (hierarchy || []).find((d) => d.code === code);
  if (!dom) return [];
  return [...(dom.targets || []), ...(dom.sub_domains || []).flatMap((sd) => sd.targets || [])];
};

// Recover the repository template a saved target name was woven from, together
// with its parameter values.
//
// The Target Picker persists only the finished sentence — no source id, no
// chosen values — so Edit works backwards from the name (see matchStatement).
// Several templates can match a short sentence, so the most specific one wins:
// the one carrying the most fixed text around its slots.
//
// Returns null when nothing matches (a custom target, or a name typed by hand),
// and the dialog then shows the plain name field exactly as before.
const recoverTemplate = (hierarchy, domainId, statement) => {
  let best = null;
  domainLibraryTargets(hierarchy, domainId).forEach((t) => {
    const hit = matchStatement(t.name, statement);
    if (hit && (!best || hit.literal > best.hit.literal)) best = { target: t, hit };
  });
  if (!best) return null;
  const raws = best.target.parameters || [];
  // Always five, exactly as the picker's Configure step offers them: the target
  // name can reference {1}..{5}, so a clinician has to be able to supply one the
  // repository left blank. A slot the name carries is prefilled with the value
  // recovered from it; the rest fall back to the authored ladder, which is what
  // the Add flow started them at.
  const specs = Array.from({ length: 5 }, (_, i) => {
    const raw = String(raws[i] || '').trim();
    // NA is the authored way of saying "no parameter" and reads as empty.
    const usable = raw && raw.toUpperCase() !== 'NA' ? raw : '';
    const woven = best.hit.slots.includes(i);
    return { raw: usable, meta: parseLadder(usable), value: woven ? best.hit.values[i] || '' : usable, woven };
  });
  return { name: best.target.name, slots: best.hit.slots, specs };
};

/**
 * The child's Mastery Prompt Level for a target while the override is on - the same
 * order the rule engine uses: the child's rule for this target, its goal, or all
 * targets, else the global one. '' when the override is off or unknown (the target
 * then follows the global rules; its own level is ignored while the override is off).
 * `state` is GET /iep/child-mastery/{childId}; `goalRowId` / `targetRowId` the
 * iep_goal / iep_target ids, if known.
 */
export const resolveDefaultMasteryLevel = (state, goalRowId, targetRowId) => {
  if (!state) return '';
  const pick = (rules) => (rules && PROMPT_LEVELS.some((p) => p.code === rules.mastery_prompt_level) ? rules.mastery_prompt_level : '');
  if (state.enabled) {
    if (state.scope === 'all') return pick(state.all_rules) || pick(state.global_rules);
    return (
      pick(targetRowId && state.target_rules ? state.target_rules[targetRowId] : null) ||
      pick(goalRowId && state.goal_rules ? state.goal_rules[goalRowId] : null) ||
      pick(state.global_rules)
    );
  }
  return '';
};

export function TargetDialog({
  open,
  onClose,
  onSave,
  initial,
  goalDomainId,
  goalLabel = '',
  goals = null,
  childId,
  skills = [],
  saving = false
}) {
  const theme = useTheme();
  const intl = useIntl();
  const [draft, setDraft] = useState(blankTarget());
  // Goal-selection mode: when a `goals` list is passed (e.g. from the therapist
  // session shortcut), the Goal field becomes a dropdown instead of a disabled
  // label, and the chosen goal is handed back to onSave's second argument.
  const goalSelectMode = Array.isArray(goals) && goals.length > 0;
  const [selectedGoalId, setSelectedGoalId] = useState('');

  // Material catalogs (central + child-specific) loaded once per dialog open.
  const [centralMaterials, setCentralMaterials] = useState([]);
  const [childMaterials, setChildMaterials] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(initial ? { ...blankTarget(), ...initial } : blankTarget());
      setSelectedGoalId('');
    }
  }, [open, initial]);

  // NEW target: pre-select the Mastery Prompt Level from the child's override,
  // else the global setting. Only fills the field while the user hasn't picked one
  // (or still has the previous default, e.g. after choosing another goal).
  const isNewTarget = !initial?.id;
  const [masteryRulesState, setMasteryRulesState] = useState(null);
  // Child's rules request: 'loading' | 'ok' | 'error' (shown in the rule fields).
  const [rulesLoad, setRulesLoad] = useState('loading');
  const [autoLevel, setAutoLevel] = useState('');
  useEffect(() => {
    if (!open || !childId) return undefined;
    let cancelled = false;
    setMasteryRulesState(null);
    setRulesLoad('loading');
    getChildMasteryRules(childId)
      .then((state) => {
        if (cancelled) return;
        setMasteryRulesState(state);
        setRulesLoad('ok');
      })
      .catch(() => !cancelled && setRulesLoad('error')); // no access / offline: the rule fields say so
    return () => {
      cancelled = true;
    };
  }, [open, childId]);
  useEffect(() => {
    if (!open || !isNewTarget || !masteryRulesState) return;
    const goalRowId = goalSelectMode ? goals.find((g) => g.id === selectedGoalId)?.iepGoalRowId : null;
    const level = resolveDefaultMasteryLevel(masteryRulesState, goalRowId);
    if (!level) return;
    setDraft((d) => (!d.masteryPromptLevel || d.masteryPromptLevel === autoLevel ? { ...d, masteryPromptLevel: level } : d));
    setAutoLevel(level);
  }, [open, isNewTarget, masteryRulesState, selectedGoalId, goals, goalSelectMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Child's mastery override OFF: every target follows the global rules, so the
  // target's Mastery Prompt Level is locked. The stored level is kept and applies
  // again once the override is turned back on. All other rule values live on the
  // Mastery Rule Override tab.
  const overrideOff = rulesLoad === 'ok' && !masteryRulesState?.enabled;
  // The level the dropdown shows: override OFF -> the global level (locked);
  // override ON -> the target's own level, else the child's level.
  const globalLevel = masteryRulesState?.global_rules?.mastery_prompt_level || 'IND';
  const childLevel = resolveDefaultMasteryLevel(
    masteryRulesState,
    goalSelectMode ? goals.find((g) => g.id === selectedGoalId)?.iepGoalRowId : draft.goalRowId,
    initial?.id
  );
  const shownLevel = overrideOff ? globalLevel : draft.masteryPromptLevel || childLevel || '';

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      setLoadingMaterials(true);
      try {
        const [central, child, metaResp] = await Promise.all([
          listCentralFileMaterials().catch(() => ({ files: [] })),
          childId ? listChildFileMaterials(childId).catch(() => ({ files: [] })) : Promise.resolve({ files: [] }),
          getMaterialsMetadata().catch(() => ({ metadata: {} }))
        ]);
        if (cancelled) return;
        const metadata = metaResp?.metadata || {};
        // Replace the raw filename `name` with the user-facing display name
        // (from metadata for central materials; from the basename of the
        // renamed file for child materials). This is what gets persisted on
        // target.materials when the supervisor saves.
        setCentralMaterials((central?.files || []).map((f) => ({ ...f, source: 'central', name: resolveMaterialName(f, metadata) })));
        setChildMaterials((child?.files || []).map((f) => ({ ...f, source: 'child', name: resolveMaterialName(f, metadata) })));
      } catch {
        if (cancelled) return;
        setCentralMaterials([]);
        setChildMaterials([]);
      } finally {
        if (!cancelled) setLoadingMaterials(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, childId]);

  const handleField = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }));

  // Setting a mastered date cascades status + tracking status to keep the three
  // mastery signals in sync (mirrors what the rule engine does automatically).
  const handleDateMastered = (e) => {
    const date = e.target.value;
    setDraft((d) => {
      if (date) {
        return {
          ...d,
          dateMastered: date,
          status: 'mastered',
          // Preserve the phase if already in maintenance (monthly/long-term are
          // further along); otherwise enter at the first maintenance phase.
          trackingStatus: MASTERY_TRACKING_PHASES.includes(d.trackingStatus) ? d.trackingStatus : FIRST_MAINTENANCE_PHASE
        };
      }
      // Date cleared — revert to active acquisition state.
      return {
        ...d,
        dateMastered: '',
        status: d.status === 'mastered' ? 'in_progress' : d.status,
        trackingStatus: MASTERY_TRACKING_PHASES.includes(d.trackingStatus) ? ACTIVE_ACQUISITION_PHASE : d.trackingStatus
      };
    });
  };

  const isEdit = Boolean(initial?.id);

  // Skill options: filtered to the parent goal's domain when provided so
  // supervisors don't see all 600+ skills at once. Falls back to all skills
  // if the goal is unassigned.
  //   • `goalDomainId` is the SNAPSHOT's domain string (e.g. "DOMAIN-MAND").
  //   • API skills carry the bare `code` (e.g. "MAND") via the
  //     `_domainCode` field added at load time in the parent component.
  // In goal-select mode the domain follows the picked goal; otherwise it's the
  // fixed domain passed by the parent.
  const selectedGoal = goalSelectMode ? goals.find((g) => g.goalId === selectedGoalId) || null : null;
  const effectiveDomainId = goalSelectMode ? selectedGoal?.domain || '' : goalDomainId;

  const skillOptions = useMemo(() => {
    if (!effectiveDomainId) return skills;
    const wantedCode = String(effectiveDomainId).replace(/^DOMAIN-/, '');
    return skills.filter((s) => s._domainCode === wantedCode);
  }, [skills, effectiveDomainId]);
  const selectedSkill = useMemo(() => skills.find((s) => s.id === draft.skillId) || null, [skills, draft.skillId]);

  // Parameters for a target that came from the repository Target Library. They
  // are re-derived from the saved name each time the dialog opens, so editing a
  // target offers the same Parameter 1..5 ladder its Add flow did — including
  // for targets created before this existed. Null = no template matched.
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    const saved = (initial?.name || '').trim();
    if (!open || !saved) {
      setTemplate(null);
      return undefined;
    }

    // What the target itself remembers, where it has it. No repository call, no
    // matching a sentence back against a row that may since have been reworded
    // or deleted - this target belongs to the child now.
    const storedTemplate = (initial?.nameTemplate || '').trim();
    const storedParams = Array.isArray(initial?.parameters) ? initial.parameters : [];
    if (storedTemplate || storedParams.some((v) => (v || '').toString().trim())) {
      setTemplate({
        name: storedTemplate || saved,
        specs: Array.from({ length: 5 }, (_, i) => {
          const value = (storedParams[i] || '').toString();
          return { raw: value, meta: parseLadder(value), value };
        })
      });
      // Edit the template, not the sentence it produced.
      if (storedTemplate) setDraft((d) => (d.name === saved ? { ...d, name: storedTemplate } : d));
      return undefined;
    }

    let cancelled = false;
    const apply = (tree) => {
      if (cancelled) return;
      const tpl = recoverTemplate(tree, effectiveDomainId, saved);
      setTemplate(tpl);
      // Edit the template, not the sentence it produced — otherwise a value
      // could never be changed twice, the slot having been overwritten the
      // first time. The woven sentence is what Save stores; with the recovered
      // values it comes back out identical to what was there.
      //
      // Only while the field is untouched: this lands whenever the repository
      // fetch happens to return, and the name box is autofocused, so anything
      // already typed into it must win.
      if (tpl) setDraft((d) => (d.name === saved ? { ...d, name: tpl.name } : d));
    };

    // Warm cache: no await, so the block is there with the dialog's first paint.
    if (skillHierarchyCache) {
      apply(skillHierarchyCache);
      return undefined;
    }
    loadSkillHierarchy()
      .then(apply)
      .catch(() => {
        // The repository is a nice-to-have here: without it the dialog is
        // simply the free-form one it has always been.
        if (!cancelled) setTemplate(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, initial, effectiveDomainId]);

  const setParamValue = (idx, value) => {
    if (!template) return;
    setTemplate({ ...template, specs: template.specs.map((sp, i) => (i === idx ? { ...sp, value } : sp)) });
  };

  // The same preview the Skill Repository shows when a target is authored, and
  // woven from the NAME FIELD rather than the repository text: a slot the
  // supervisor adds by hand ({1}, {x}, …) fills from the parameters just like an
  // authored one. This is also exactly what Save stores.
  const parameterPreview = useMemo(() => (template ? weaveStatement(draft.name || '', template.specs) : null), [template, draft.name]);

  // How many filled parameters the name has no slot for: a value that was not
  // woven in has nowhere to go, since weaveStatement deliberately appends
  // nothing. Counting substitutions against filled values keeps the warning true
  // as the supervisor adds or removes slots.
  const unwrittenParams = useMemo(() => {
    if (!template || !parameterPreview) return 0;
    const filled = template.specs.filter((sp) => (sp.value || '').toString().trim()).length;
    const written = parameterPreview.segments.filter((seg) => seg.type === 'value').length;
    return Math.max(0, filled - written);
  }, [template, parameterPreview]);

  // Material options combined + grouped via Autocomplete `groupBy`.
  const materialOptions = useMemo(() => [...childMaterials, ...centralMaterials], [centralMaterials, childMaterials]);
  // Materials stored on the draft as path+source identifiers; rehydrate the
  // full objects from the loaded lists for the Autocomplete `value`.
  const selectedMaterials = useMemo(() => {
    return (draft.materials || [])
      .map((m) => materialOptions.find((opt) => opt.path === m.path && opt.source === m.source) || m)
      .filter(Boolean);
  }, [draft.materials, materialOptions]);

  const days = computeDays(draft);

  // Mastery consistency: the three mastery signals must move together, exactly
  // as the mastery rule engine maintains them —
  //   status "Mastered"  ⟺  a maintenance tracking phase  ⟺  a "Date mastered".
  // This blocks the inconsistent states the engine never produces (e.g. status
  // "Mastered" + a date while the tracking phase is still "Not Introduced", i.e.
  // a target that never went through acquisition and so never met criteria, yet
  // shows a green mastered date). Set all three, or none.
  const isMastered = draft.status === 'mastered';
  const inMaintenancePhase = MASTERY_TRACKING_PHASES.includes(draft.trackingStatus);
  const hasMasteredDate = Boolean(draft.dateMastered);
  const masteryError =
    isMastered === inMaintenancePhase && isMastered === hasMasteredDate ? '' : intl.formatMessage({ id: 'goalsTargets.masteryError' });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? intl.formatMessage({ id: 'goalsTargets.editTarget' }) : intl.formatMessage({ id: 'goalsTargets.addTarget' })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Parent goal. In goal-select mode (therapist session shortcut) this is
              an editable dropdown of the child's goals; otherwise a read-only
              label showing which goal the target is being added to. */}
          {goalSelectMode ? (
            <Autocomplete
              size="small"
              options={goals}
              value={selectedGoal}
              onChange={(_, val) => setSelectedGoalId(val?.goalId || '')}
              getOptionLabel={(o) => (o ? [o.goalId, o.name].filter(Boolean).join(' — ') : '')}
              isOptionEqualToValue={(a, b) => a.goalId === b.goalId}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={intl.formatMessage({ id: 'goalsTargets.goalLabel' })}
                  placeholder={intl.formatMessage({ id: 'goalsTargets.selectGoalPlaceholder' })}
                />
              )}
            />
          ) : (
            goalLabel && (
              <TextField
                fullWidth
                size="small"
                label={intl.formatMessage({ id: 'goalsTargets.goalLabel' })}
                value={goalLabel}
                disabled
                multiline
                maxRows={3}
              />
            )
          )}

          <TextField
            fullWidth
            size="small"
            label={intl.formatMessage({ id: 'goalsTargets.targetName' })}
            value={draft.name}
            onChange={handleField('name')}
            helperText={template ? intl.formatMessage({ id: 'goalsTargets.targetNameSlots' }) : ''}
            autoFocus
          />

          {/* Parameters — shown when the target name still matches the Target
              Library template it was added from. Same fields as the picker's
              Configure step; the name field above holds the template and the
              preview below shows what Save will store. */}
          {template && (
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block' }}>
                {intl.formatMessage({ id: 'goalsTargets.parameters' })}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                {intl.formatMessage({ id: 'goalsTargets.parametersFromTemplate' }, { template: template.name })}
              </Typography>
              <Grid container spacing={1.5}>
                {template.specs.map((sp, idx) => (
                  <Grid key={idx} size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      maxRows={3}
                      label={intl.formatMessage({ id: 'goalsTargets.parameterN' }, { n: idx + 1 })}
                      value={sp.value}
                      onChange={(e) => setParamValue(idx, e.target.value)}
                      // The authored ladder steps are suggestions; any value is allowed.
                      helperText={sp.meta.options.length > 1 ? sp.meta.options.join(' / ') : ''}
                    />
                  </Grid>
                ))}

                <Grid size={12}>
                  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                      {intl.formatMessage({ id: 'goalsTargets.parameterPreview' })}
                    </Typography>
                    <Typography variant="body2" component="div">
                      {parameterPreview?.segments.map((seg, i) =>
                        seg.type === 'value' ? (
                          <Box
                            key={i}
                            component="span"
                            sx={{
                              color: 'primary.dark',
                              fontWeight: 700,
                              fontStyle: 'italic',
                              textDecoration: 'underline',
                              textUnderlineOffset: 3
                            }}
                          >
                            {seg.text}
                          </Box>
                        ) : (
                          <Box key={i} component="span">
                            {seg.text}
                          </Box>
                        )
                      )}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Said once, under the row, and read off the live preview rather than the
                    repository template — a slot typed into the name by hand counts. Five copies
                    of this line, one per field, drowned the dialog. It sits inside the Grid so
                    the container's row margin can't clip it. */}
                {unwrittenParams > 0 && (
                  <Grid size={12}>
                    <Typography variant="caption" color="text.secondary">
                      {intl.formatMessage({ id: 'goalsTargets.parameterNoSlot' })}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* Skill — pulled from the Skills Repository (clinical.skill).
              `o.id` is the row UUID (stored on iep_target.skill_id); `o.code`
              is the human label like "SK-MAND-001" the supervisor recognises. */}
          <Autocomplete
            size="small"
            options={skillOptions}
            value={selectedSkill}
            onChange={(_, val) => setDraft((d) => ({ ...d, skillId: val?.id || '' }))}
            getOptionLabel={(o) => (o ? `${o.code} — ${o.name}` : '')}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={
                  effectiveDomainId
                    ? intl.formatMessage({ id: 'goalsTargets.skillFilteredLabel' })
                    : intl.formatMessage({ id: 'goalsTargets.skillLabel' })
                }
                placeholder={intl.formatMessage({ id: 'goalsTargets.skillPlaceholder' })}
              />
            )}
          />

          {/* Materials — multi-select from central + child-specific */}
          <Autocomplete
            multiple
            size="small"
            disableCloseOnSelect
            options={materialOptions}
            value={selectedMaterials}
            loading={loadingMaterials}
            onChange={(_, val) =>
              setDraft((d) => ({
                ...d,
                materials: val.map((m) => ({
                  source: m.source,
                  path: m.path,
                  name: m.name,
                  url: m.url,
                  category: m.category || null
                }))
              }))
            }
            getOptionLabel={(o) => o?.name || ''}
            isOptionEqualToValue={(a, b) => a.path === b.path && a.source === b.source}
            groupBy={(o) =>
              o.source === 'child'
                ? intl.formatMessage({ id: 'goalsTargets.childSpecificMaterials' })
                : intl.formatMessage({ id: 'goalsTargets.centralMaterialRepo' })
            }
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              return (
                <Box component="li" key={key} {...rest} sx={{ gap: 1.25 }}>
                  <Avatar
                    src={materialThumbnailUrl(option)}
                    alt={option.name}
                    variant="rounded"
                    sx={{ width: 28, height: 28, bgcolor: alpha(theme.palette.primary.main, 0.08) }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {option.name}
                    </Typography>
                    {option.category && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {option.category}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
            renderTags={(values, getTagProps) =>
              values.map((option, index) => (
                <Chip
                  size="small"
                  variant="outlined"
                  avatar={<Avatar src={materialThumbnailUrl(option)} alt={option.name} />}
                  label={option.name}
                  {...getTagProps({ index })}
                  key={`${option.source}-${option.path}`}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={intl.formatMessage({ id: 'goalsTargets.materialsLabel' })}
                placeholder={
                  loadingMaterials
                    ? intl.formatMessage({ id: 'goalsTargets.loadingMaterialsPlaceholder' })
                    : intl.formatMessage({ id: 'goalsTargets.materialsSearchPlaceholder' })
                }
                helperText={intl.formatMessage({ id: 'goalsTargets.materialsHelperText' })}
              />
            )}
          />

          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label={intl.formatMessage({ id: 'goalsTargets.description' })}
            value={draft.description}
            onChange={handleField('description')}
          />

          {/* SD (discriminative stimulus) — optional, multi-line. Whatever is
              entered here is shown as a tooltip against this target on the
              therapist's session data-collection sheets (Cold Probe / Multi-Trial). */}
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label={intl.formatMessage({ id: 'goalsTargets.sdInstruction', defaultMessage: 'SD (Discriminative Stimulus)' })}
            placeholder={intl.formatMessage({
              id: 'goalsTargets.sdInstructionPlaceholder',
              defaultMessage: 'Instruction / cue to deliver — shown as a tooltip on the data sheet'
            })}
            value={draft.sdInstruction}
            onChange={handleField('sdInstruction')}
          />

          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={intl.formatMessage({ id: 'goalsTargets.dateIntroducedLabel' })}
                InputLabelProps={{ shrink: true }}
                value={draft.dateIntroduced || ''}
                onChange={handleField('dateIntroduced')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={intl.formatMessage({ id: 'goalsTargets.dateMasteredLabel' })}
                InputLabelProps={{ shrink: true }}
                value={draft.dateMastered || ''}
                onChange={handleDateMastered}
                inputProps={{ min: draft.dateIntroduced || undefined }}
                error={Boolean(masteryError)}
                helperText={masteryError || undefined}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label={intl.formatMessage({ id: 'goalsTargets.tableDays' })}
                value={days === null ? '' : days}
                InputProps={{ readOnly: true }}
                helperText={
                  days === null
                    ? intl.formatMessage({ id: 'goalsTargets.daysAutoCalc' })
                    : draft.dateMastered
                      ? intl.formatMessage({ id: 'goalsTargets.daysToMasteryHint' })
                      : intl.formatMessage({ id: 'goalsTargets.daysSinceIntro' })
                }
              />
            </Grid>
          </Grid>

          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label={intl.formatMessage({ id: 'goalsTargets.location' })}
                placeholder={intl.formatMessage({ id: 'goalsTargets.locationPlaceholder' })}
                value={draft.location}
                onChange={handleField('location')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label={intl.formatMessage({ id: 'goalsTargets.people' })}
                placeholder={intl.formatMessage({ id: 'goalsTargets.peoplePlaceholder' })}
                value={draft.people}
                onChange={handleField('people')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label={intl.formatMessage({ id: 'goalsTargets.stimuli' })}
                placeholder={intl.formatMessage({ id: 'goalsTargets.stimuliPlaceholder' })}
                value={draft.stimuli}
                onChange={handleField('stimuli')}
              />
            </Grid>
          </Grid>

          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={intl.formatMessage({ id: 'goalsTargets.statusHeader' })}
                value={draft.status || ''}
                onChange={handleField('status')}
                error={Boolean(masteryError)}
              >
                {TARGET_STATUS_VALUES.map((v) => (
                  <MenuItem key={v} value={v}>
                    {TARGET_STATUS_INTL_KEYS[v] ? intl.formatMessage({ id: TARGET_STATUS_INTL_KEYS[v] }) : v.replace(/_/g, ' ')}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={intl.formatMessage({ id: 'goalsTargets.trackingStatusLabel' })}
                value={draft.trackingStatus || ''}
                onChange={handleField('trackingStatus')}
                error={Boolean(masteryError)}
              >
                {TRACKING_STATUS_VALUES.map((v) => (
                  <MenuItem key={v} value={v}>
                    {TRACKING_STATUS_INTL_KEYS[v] ? intl.formatMessage({ id: TRACKING_STATUS_INTL_KEYS[v] }) : v.replace(/_/g, ' ')}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {/* Default Controlling Prompts — multi-select checkboxes. Whatever is
              picked here pre-selects the controlling prompt in the therapist's
              Cold Probe / Multi-Trial session sheets (the FIRST selected code is
              auto-selected there, in PROMPT_LEVELS order), so the usual prompt
              for this target doesn't have to be re-picked every session. All
              prompt options still remain selectable during the session, and a
              target with none selected keeps the legacy "pick each time" flow. */}
          <Grid container spacing={1.5}>
            {/* Multi-select dropdown (was a row of checkboxes). Picked codes show as
                chips; the value is kept in canonical PROMPT_LEVELS order so the
                "first" default the session sheets pre-select is deterministic. */}
            <Grid size={{ xs: 12, sm: 7 }}>
              <TextField
                select
                fullWidth
                size="small"
                label={intl.formatMessage({ id: 'goalsTargets.defaultControllingPrompts', defaultMessage: 'Default Controlling Prompts' })}
                value={Array.isArray(draft.defaultControllingPrompts) ? draft.defaultControllingPrompts : []}
                onChange={(e) => {
                  const picked = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                  setDraft((d) => ({
                    ...d,
                    defaultControllingPrompts: PROMPT_LEVELS.map((x) => x.code).filter((c) => picked.includes(c))
                  }));
                }}
                SelectProps={{
                  multiple: true,
                  displayEmpty: true,
                  renderValue: (picked) =>
                    picked.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        {intl.formatMessage({ id: 'goalsTargets.noneSelected', defaultMessage: 'None' })}
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {picked.map((code) => (
                          <Chip key={code} label={code} size="small" sx={{ height: 22, fontSize: '0.6875rem', fontWeight: 600 }} />
                        ))}
                      </Box>
                    )
                }}
                InputLabelProps={{ shrink: true }}
              >
                {PROMPT_LEVELS.map((p) => (
                  <MenuItem key={p.code} value={p.code}>
                    <Checkbox size="small" checked={(draft.defaultControllingPrompts || []).includes(p.code)} sx={{ p: 0.5, mr: 1 }} />
                    {`${p.code} — ${p.label}`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {/* Mastery prompt level: the ONE level that counts toward mastery for this
                target. Shows the level in effect - the global one (locked) while the
                child's override is off, else the target's own level or the child's.
                With the override on it can be changed for this target. */}
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField
                select
                fullWidth
                size="small"
                label={intl.formatMessage({ id: 'goalsTargets.masteryPromptLevel', defaultMessage: 'Mastery Prompt Level' })}
                value={shownLevel}
                disabled={overrideOff || (Boolean(childId) && rulesLoad === 'loading')}
                onChange={(e) => setDraft((d) => ({ ...d, masteryPromptLevel: e.target.value }))}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (code) => {
                    const p = PROMPT_LEVELS.find((x) => x.code === code);
                    if (p) return `${p.code} — ${p.label}`;
                    return rulesLoad === 'loading' && childId
                      ? intl.formatMessage({ id: 'goalsTargets.ruleLoading', defaultMessage: 'Loading child rules…' })
                      : '';
                  }
                }}
                InputLabelProps={{ shrink: true }}
                helperText={(() => {
                  if (overrideOff)
                    return intl.formatMessage({
                      id: 'goalsTargets.masteryFromGlobal',
                      defaultMessage: "Global setting - this child's mastery override is off"
                    });
                  if (draft.masteryPromptLevel && draft.masteryPromptLevel !== childLevel)
                    return intl.formatMessage({ id: 'goalsTargets.masteryForTarget', defaultMessage: 'Set for this target' });
                  if (rulesLoad === 'ok')
                    return intl.formatMessage({ id: 'goalsTargets.masteryFromChild', defaultMessage: "From this child's mastery rules" });
                  return intl.formatMessage({
                    id: 'goalsTargets.masteryPromptLevelHint',
                    defaultMessage: 'Which prompt level is considered for mastery for this target'
                  });
                })()}
              >
                {PROMPT_LEVELS.map((p) => (
                  <MenuItem key={p.code} value={p.code}>{`${p.code} — ${p.label}`}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {/* Exclude from re-compute: when the mastery rules change, the rule engine
              re-evaluates every target in the current IEP; a ticked target is left
              alone (e.g. a mastered target stays mastered). Stored only for now. */}
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={Boolean(draft.excludeFromRecompute)}
                onChange={(e) => setDraft((d) => ({ ...d, excludeFromRecompute: e.target.checked }))}
              />
            }
            label={
              <Typography variant="body2">
                {intl.formatMessage({ id: 'goalsTargets.excludeFromRecompute', defaultMessage: 'Exclude from rule engine' })}
              </Typography>
            }
          />

          {/* Approval — read-only display. Hidden in goal-select (therapist)
              mode: a therapist doesn't set these; the target stays "Pending"
              until a supervisor approves it from the Goals & Targets tab.
              Shown for the supervisor, who is approving on save. */}
          {!goalSelectMode && (
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={intl.formatMessage({ id: 'goalsTargets.approvedByLabel' })}
                  value={draft.approvedByName || ''}
                  InputProps={{ readOnly: true }}
                  placeholder={intl.formatMessage({ id: 'goalsTargets.pendingApprovalPlaceholder' })}
                  helperText={draft.approvedByName ? '' : intl.formatMessage({ id: 'goalsTargets.pendingApprovalHint' })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={intl.formatMessage({ id: 'goalsTargets.approvedDateLabel' })}
                  value={draft.approvedDate || ''}
                  InputProps={{ readOnly: true }}
                  placeholder="—"
                />
              </Grid>
            </Grid>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {intl.formatMessage({ id: 'goalsTargets.cancel' })}
        </Button>
        <Button
          variant="contained"
          disabled={saving || !draft.name?.trim() || (goalSelectMode && !selectedGoalId) || Boolean(masteryError)}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          onClick={() => {
            if (saving) return;
            const now = new Date().toISOString();
            // The plan carries the finished sentence — a name with "{1}" still in
            // it would read as a placeholder on the data sheet.
            const name = template ? weaveStatement(draft.name || '', template.specs).statement : draft.name;
            onSave(
              {
                ...draft,
                name,
                // Kept so the next Edit opens on the template again rather than on
                // the sentence, and so the values are the ones this child was given.
                nameTemplate: draft.name || '',
                parameters: template ? template.specs.map((sp) => (sp.value ?? '').toString()) : draft.parameters || [],
                id: draft.id || makeTargetId(),
                createdAt: draft.createdAt || now,
                updatedAt: now
              },
              selectedGoal
            );
          }}
        >
          {saving
            ? intl.formatMessage({ id: 'form.button.saving', defaultMessage: 'Saving…' })
            : intl.formatMessage({ id: 'goalsTargets.save' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

TargetDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  initial: PropTypes.object,
  goalDomainId: PropTypes.string,
  goalLabel: PropTypes.string,
  goals: PropTypes.array,
  childId: PropTypes.string,
  skills: PropTypes.array,
  saving: PropTypes.bool
};

// Backend iep_target row → frontend draft shape used by TargetDialog and the
// rendering loop. Keeps both the relational row id (UUID) and the legacy
// "T-…" target_code so create-then-edit flows don't lose identity.
//
// Mastery reconciliation: if achieved_date is present, status and tracking_status
// are derived from MASTERY_TRACKING_PHASES / FIRST_MAINTENANCE_PHASE so stale DB
// rows (where the rule engine hasn't yet synced these fields) show correctly.
export const backendTargetToDraft = (t) => {
  const hasMasteredDate = Boolean(t.achieved_date);
  const rawStatus = t.status?.code || 'in_progress';
  const rawTracking = t.tracking_status || 'not_introduced';
  return {
    id: t.id, // UUID — used as React key + the id we pass back on update
    targetCode: t.target_code || '',
    name: t.title || '',
    skillId: t.skill_id || '',
    materials: Array.isArray(t.materials) ? t.materials : [],
    parameters: Array.isArray(t.parameters) ? t.parameters : [],
    nameTemplate: t.title_template || '',
    description: t.description || '',
    dateIntroduced: t.start_date || '',
    dateMastered: t.achieved_date || '',
    location: t.location || '',
    people: t.people || '',
    stimuli: t.stimuli || '',
    status: hasMasteredDate ? 'mastered' : rawStatus,
    trackingStatus: hasMasteredDate && !MASTERY_TRACKING_PHASES.includes(rawTracking) ? FIRST_MAINTENANCE_PHASE : rawTracking,
    defaultControllingPrompts: Array.isArray(t.default_controlling_prompts) ? t.default_controlling_prompts : [],
    masteryPromptLevel: t.mastery_prompt_level || '',
    excludeFromRecompute: Boolean(t.exclude_from_recompute),
    // The iep_goal row id - finds the child's goal-level rules in Edit Target.
    goalRowId: t.goal_id || '',
    sdInstruction: t.sd_instruction || '',
    approvedById: t.approved_by_id || '',
    approvedByName: t.approved_by_name || '',
    approvedDate: t.approved_date || '',
    declinedById: t.declined_by_id || '',
    declinedByName: t.declined_by_name || '',
    declinedDate: t.declined_date || '',
    createdAt: t.created_at || null,
    updatedAt: t.updated_at || null
  };
};

// Frontend draft → backend create/update payload. `target_code` is the
// stable human id ("T-…"); the row's UUID stays implicit (PK).
export const draftToTargetPayload = (draft) => ({
  target_code: draft.targetCode || draft.id || makeTargetId(),
  title: (draft.name || '').trim() || draft.id || 'Untitled',
  description: draft.description || null,
  skill_id: draft.skillId || null,
  materials: Array.isArray(draft.materials) ? draft.materials : [],
  // Always sent, so clearing a parameter persists instead of being ignored.
  parameters: Array.isArray(draft.parameters) ? draft.parameters : [],
  title_template: draft.nameTemplate || null,
  start_date: draft.dateIntroduced || null,
  achieved_date: draft.dateMastered || null,
  location: draft.location || null,
  people: draft.people || null,
  stimuli: draft.stimuli || null,
  status_code: draft.status || 'in_progress',
  tracking_status: draft.trackingStatus || null,
  // Default controlling prompt codes (e.g. ["PVP","IND"]) — pre-selected in the
  // session Cold Probe / Multi-Trial sheets. Always sent so clearing them persists.
  default_controlling_prompts: Array.isArray(draft.defaultControllingPrompts) ? draft.defaultControllingPrompts : [],
  // Always sent, so choosing "Follow child / global" clears it (null) and unticking persists.
  mastery_prompt_level: draft.masteryPromptLevel || null,
  exclude_from_recompute: Boolean(draft.excludeFromRecompute),
  // SD (discriminative stimulus) free-text — always sent so clearing it persists.
  sd_instruction: draft.sdInstruction || null,
  // Approval (sent only when the draft carries it — i.e. supervisor add, or a
  // supervisor approving a pending target). Therapist adds leave these null.
  approved_by_id: draft.approvedById || null,
  approved_by_name: draft.approvedByName || null,
  approved_date: draft.approvedDate || null,
  declined_by_id: draft.declinedById || null,
  declined_by_name: draft.declinedByName || null,
  declined_date: draft.declinedDate || null
});

// `readOnly` removes every write control — add goal, add target, edit, delete
// and the approve/decline review actions — leaving the plan fully readable.
// Used by the caregiver profile: a parent may see the plan but not change it,
// and the API refuses their writes regardless, so an edit control here could
// only ever produce an error.
const GoalsTargetsTab = ({ childId, initialIEPVersionId, readOnly: readOnlyProp = false }) => {
  const theme = useTheme();
  const intl = useIntl();
  const { user } = useAuth();
  // Full-Read on Goals & Targets (or the parent Child Profiles feature): read the
  // assigned goals only. "Add New Goal" is greyed out; other write controls hide.
  const { readOnly: isCodeReadOnly } = usePageAccess();
  const readOnly = readOnlyProp || isCodeReadOnly('child_goals_targets');

  // Current supervisor identity — used to auto-approve targets added/approved
  // here. (Adds from the therapist session use a different code path and stay
  // pending.)
  const supervisorName =
    user?.person?.display_name ||
    `${user?.first_name || user?.person?.first_name || ''} ${user?.last_name || user?.person?.last_name || ''}`.trim() ||
    user?.name ||
    user?.email ||
    'Supervisor';
  const supervisorPersonId = user?.person_id || user?.person?.id || null;
  const todayIso = () => new Date().toISOString().slice(0, 10);

  const [versions, setVersions] = useState([]); // [{ id, version_number, change_summary, created_at, iep_code, iep_title }]
  const [selectedVersionId, setSelectedVersionId] = useState(initialIEPVersionId || '');
  const [snapshot, setSnapshot] = useState(null); // full snapshot_data of selected version
  const [overlay, setOverlay] = useState({}); // targets_overlay map
  const [loadingVersions, setLoadingVersions] = useState(true);
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Add/Edit target dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogGoalId, setDialogGoalId] = useState('');
  const [dialogGoalDomainId, setDialogGoalDomainId] = useState('');
  const [dialogGoalLabel, setDialogGoalLabel] = useState('');
  const [dialogInitial, setDialogInitial] = useState(null);

  // Target detail dialog. The supervisor list shows targets in a compact
  // table row (mirroring the therapist Target Tracking datasheet); clicking
  // a row opens this dialog with the full picture — description, materials
  // with thumbnails, location/people/stimuli, and computed days.
  const [detailTarget, setDetailTarget] = useState(null); // { goal, target }
  const openTargetDetail = (goal, target) => setDetailTarget({ goal, target });
  const closeTargetDetail = () => setDetailTarget(null);

  // Styled confirmation dialog (replaces native window.confirm for
  // delete/decline). Shape: { title, message, confirmLabel, confirmColor, action }.
  const [confirmDialog, setConfirmDialog] = useState(null);

  // Domain-level accordion state. null = all domains expanded (the default).
  // Becomes a Set of expanded domain IDs after first user or global interaction.
  const [expandedDomains, setExpandedDomains] = useState(null);

  // 1. Load IEP versions on mount / childId change.
  useEffect(() => {
    if (!childId) return;
    let cancelled = false;
    const load = async () => {
      setLoadingVersions(true);
      setError(null);
      try {
        const res = await listClientIepVersions(childId);
        if (cancelled) return;
        const list = Array.isArray(res) ? res : res?.items || [];
        setVersions(list);
        // Pick the initial-supplied version if it exists in the list, otherwise
        // default to the version ACTIVE for today (date window covers today) —
        // not just the highest version number — so a future-dated version doesn't
        // open by default. Falls back to newest if none is currently active.
        if (list.length > 0) {
          const wanted = list.find((v) => v.id === initialIEPVersionId);
          const active = selectActiveVersion(list) || list[0];
          setSelectedVersionId(wanted ? wanted.id : active.id);
        } else {
          setSelectedVersionId('');
        }
      } catch (err) {
        if (cancelled) return;
        console.warn('[GoalsTargetsTab] versions load failed:', err?.message);
        setError(err?.response?.data?.detail || err?.message || 'Failed to load IEP versions');
        setVersions([]);
      } finally {
        if (!cancelled) setLoadingVersions(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [childId, initialIEPVersionId]);

  // The iep_id of the currently selected version (taken from the version
  // row). Needed for all the new relational endpoints (they're scoped by
  // iep_id, with iep_version_id as a query filter).
  const selectedIepId = useMemo(() => {
    const v = versions.find((x) => x.id === selectedVersionId);
    return v?.iep_id || '';
  }, [versions, selectedVersionId]);

  // Snapshot goalCode → iep_goal.id (UUID). Populated after the loader runs;
  // used to translate the snapshot's stable "G-001" identifiers into the
  // database UUID that the target endpoints need.
  const [goalRowIdByCode, setGoalRowIdByCode] = useState({});

  // Repository-driven builders (replace IEP-snapshot extraction for authoring).
  const [goalBuilderOpen, setGoalBuilderOpen] = useState(false);
  // Free-form custom goal add/edit (shared GoalDetailsModal). null = closed;
  // { initial: null } = add; { initial: goal } = edit.
  const [customGoalModal, setCustomGoalModal] = useState(null);
  const [targetPicker, setTargetPicker] = useState(null); // { goalCode, domainCode, domainName }
  // Goals now come from the live iep_goal rows (authored here), NOT the IEP
  // snapshot. Each: { goalId, domain, name, description, baseline, startDate,
  // endDate, targetCriteria, status }.
  const [liveGoals, setLiveGoals] = useState([]);

  // Skills catalogue (clinical.skill rows). Loaded once when the tab mounts —
  // used for the target dialog's autocomplete AND for resolving the skill
  // name to display next to each target row. Each row gets `_domainCode`
  // tacked on so the dialog filter doesn't have to also fetch domains.
  const [skills, setSkills] = useState([]);
  // Live skill domains (from clinical.skill_domain), as { id: 'DOMAIN-CODE', name }.
  // Drives goal grouping + labels so any domain (incl. newly added ones) shows.
  const [apiDomains, setApiDomains] = useState([]);
  const skillById = useMemo(() => {
    const m = new Map();
    for (const s of skills) m.set(s.id, s);
    return m;
  }, [skills]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Warm the Target Library alongside these, so the Edit dialog's
        // Parameters block is ready before anyone opens it. Fire-and-forget:
        // nothing here depends on it.
        loadSkillHierarchy().catch(() => {});
        const [domainList, skillList] = await Promise.all([
          // Fetch domains so we can attach the bare `code` ("MAND", "TACT") to
          // each skill — the dialog filters by code to match the snapshot's
          // "DOMAIN-MAND" string after stripping the prefix.
          (await import('services/api/skills')).listSkillDomains().catch(() => []),
          listSkills({}).catch(() => [])
        ]);
        if (cancelled) return;
        const codeById = new Map(domainList.map((d) => [d.id, d.code]));
        setSkills(skillList.map((s) => ({ ...s, _domainCode: codeById.get(s.domain_id) || '' })));
        // Build goal-domain options (id format matches the snapshot's "DOMAIN-CODE").
        const seen = new Set();
        setApiDomains(
          domainList
            .filter((d) => d.code && d.name)
            .map((d) => ({ id: `DOMAIN-${d.code}`, name: d.name }))
            .filter((d) => (seen.has(d.name.toLowerCase()) ? false : seen.add(d.name.toLowerCase())))
        );
      } catch {
        if (!cancelled) setSkills([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 2. Load goals (live iep_goal rows) + their targets for the selected version.
  //    Goals are authored HERE via the repository builder — we no longer extract
  //    them from the IEP snapshot or self-heal snapshot goals into rows. The
  //    version snapshot is still read for header metadata (child name, window).
  const reloadSnapshot = useCallback(async () => {
    if (!selectedVersionId) {
      setSnapshot(null);
      setOverlay({});
      setGoalRowIdByCode({});
      setLiveGoals([]);
      return;
    }
    setLoadingSnapshot(true);
    setError(null);
    try {
      // 2a. Version — header metadata only (child name, IEP window, iep_id).
      const versionRes = await getIepVersion(selectedVersionId);
      setSnapshot(versionRes?.snapshot_data || {});

      const iepId = versionRes?.iep_id;
      if (!iepId) {
        setOverlay({});
        setGoalRowIdByCode({});
        setLiveGoals([]);
        return;
      }

      // 2b. Live goals from the iep_goal table (source of truth for the list).
      const mapRows = (rows) => {
        const codeToId = {};
        const mapped = [];
        for (const g of rows) {
          const code = g.goal_code;
          if (!code) continue;
          codeToId[code] = g.id;
          mapped.push({
            goalId: code,
            // Normalize legacy "DOMAIN-MAND" style codes to the bare repo code so
            // the same clinical domain never renders as two separate groups.
            domain: (g.skill_domain_code || '').replace(/^DOMAIN-/i, '') || UNASSIGNED_DOMAIN_ID,
            name: g.description || g.title || '',
            description: g.description || '',
            // The narrative under the statement; `description` above is the
            // statement itself.
            goalDescription: g.goal_description || '',
            serviceCode: g.service_code || '',
            activities: activityLines(g.activities),
            baseline: g.baseline || '',
            startDate: g.start_date || null,
            endDate: g.target_date || null,
            targetCriteria: g.target_criteria || '',
            // status may arrive as a NamedReference {id, code, name} or a plain
            // string. Normalize to lowercase snake_case so GOAL_STATUS_INTL_KEYS
            // lookup always succeeds regardless of backend casing ('Active' → 'active').
            status: (typeof g.status === 'string' ? g.status : g.status?.code || g.status?.name || 'in_progress')
              .toLowerCase()
              .replace(/\s+/g, '_')
          });
        }
        return { codeToId, mapped };
      };
      const fetchGoalRows = async () => {
        const res = await listGoals(iepId, { iep_version_id: selectedVersionId, page_size: 500 });
        return res?.items || res || [];
      };

      let { codeToId, mapped } = mapRows(await fetchGoalRows());

      // 2b-i. Self-heal: goals present in the version SNAPSHOT but with no live
      // iep_goal row (e.g. an IEP-form save whose live-row sync silently failed)
      // are otherwise invisible here — and a target could never be attached to
      // them ("a target was added but never showed up"). Recreate the missing
      // rows once, idempotently by goal_code, then re-read. Never writes in the
      // read-only (caregiver) view.
      const snapGoals = Array.isArray(versionRes?.snapshot_data?.iepGoals) ? versionRes.snapshot_data.iepGoals : [];
      const missing = snapGoals.filter((sg) => {
        const text = (sg?.name || sg?.description || '').trim();
        return sg?.goalId && text && !codeToId[sg.goalId];
      });
      if (missing.length > 0 && !readOnly) {
        let createdAny = false;
        for (const sg of missing) {
          try {
            await createGoal(iepId, {
              iep_id: iepId,
              iep_version_id: selectedVersionId,
              goal_code: sg.goalId,
              skill_domain_code: (sg.domain || '').replace(/^DOMAIN-/i, '').trim() || null,
              service_code: sg.serviceCode || null,
              description: (sg.name || sg.description || '').trim(),
              goal_description: sg.goalDescription || null,
              activities: activityLines(sg.activities),
              baseline: sg.baseline || null,
              target_criteria: sg.targetCriteria || null,
              start_date: sg.startDate || null,
              target_date: sg.endDate || null,
              status_code: 'active'
            });
            createdAny = true;
          } catch (healErr) {
            // A pre-existing (e.g. soft-deleted) row with this code 409s — that
            // goal was deliberately removed, so leave it out. Others just log.
            console.warn(`[GoalsTargetsTab] snapshot goal self-heal failed for ${sg.goalId}:`, healErr?.message);
          }
        }
        if (createdAny) ({ codeToId, mapped } = mapRows(await fetchGoalRows()));
      }

      setGoalRowIdByCode(codeToId);
      setLiveGoals(mapped);

      // 2c. Targets for each goal (overlay keyed by goal code).
      const nextOverlay = {};
      await Promise.all(
        Object.entries(codeToId).map(async ([code, goalId]) => {
          try {
            const res = await listTargets(iepId, goalId, { page_size: 500 });
            const rows = res?.items || res || [];
            nextOverlay[code] = rows.map(backendTargetToDraft);
          } catch (err) {
            console.warn(`[GoalsTargetsTab] listTargets(${code}) failed:`, err?.message);
            nextOverlay[code] = [];
          }
        })
      );
      setOverlay(nextOverlay);

      // 2d. Auto-complete goals whose every ACTIVE target is now mastered.
      //     Declined targets are excluded — they're kept for tracking only and
      //     must not block (or be required for) goal completion.
      const toComplete = mapped.filter((g) => {
        if (g.status === GOAL_DB_STATUS_ACHIEVED || g.status === GOAL_SNAPSHOT_STATUS_COMPLETED) return false;
        const targets = (nextOverlay[g.goalId] || []).filter((t) => !t.declinedDate);
        return targets.length > 0 && targets.every((t) => Boolean(t.dateMastered));
      });
      if (toComplete.length > 0) {
        const done = new Set(toComplete.map((g) => g.goalId));
        await Promise.all(
          toComplete.map((g) => updateGoal(iepId, codeToId[g.goalId], { status_code: GOAL_DB_STATUS_ACHIEVED }).catch(() => null))
        );
        setLiveGoals((prev) => prev.map((g) => (done.has(g.goalId) ? { ...g, status: GOAL_DB_STATUS_ACHIEVED } : g)));
      }
    } catch (err) {
      console.warn('[GoalsTargetsTab] version load failed:', err?.message);
      setError(err?.response?.data?.detail || err?.message || 'Failed to load IEP version');
      setSnapshot(null);
      setOverlay({});
      setGoalRowIdByCode({});
      setLiveGoals([]);
    } finally {
      setLoadingSnapshot(false);
    }
  }, [selectedVersionId, readOnly]);

  useEffect(() => {
    reloadSnapshot();
  }, [reloadSnapshot]);

  // Reset accordion state when the user switches IEP version so the new
  // version always opens fully expanded (matching the default behaviour).
  useEffect(() => {
    setExpandedDomains(null);
    setExpandedServices(null);
  }, [selectedVersionId]);

  // Service filter — '' is every service. 'none' is its own choice rather than a
  // blank option, because "goals nobody has assigned a service to yet" is a list
  // a supervisor actively wants (it is the work queue for tagging them).
  const [serviceFilter, setServiceFilter] = useState('');

  // Which service folders are open. null means "all of them" — the same
  // convention expandedDomains uses, so a freshly loaded version shows
  // everything without having to enumerate the groups before they exist.
  const [expandedServices, setExpandedServices] = useState(null);

  // Goals left after the service filter. Kept separate from the grouping below
  // so the "N goals" chip and the expand/collapse set describe what is on
  // screen, not what is in the version.
  const visibleGoals = useMemo(() => {
    if (!serviceFilter) return liveGoals;
    if (serviceFilter === 'none') return liveGoals.filter((g) => !g.serviceCode);
    return liveGoals.filter((g) => g.serviceCode === serviceFilter);
  }, [liveGoals, serviceFilter]);

  // Group a list of goals by domain id, preserving the order: live skill-repo
  // domains first, then any domain present on a goal but not in the repo (so
  // nothing is ever hidden), then unassigned at the end.
  const groupByDomain = useCallback(
    (goals) => {
      const map = new Map();
      for (const g of goals) {
        const did = g.domain || UNASSIGNED_DOMAIN_ID;
        if (!map.has(did)) map.set(did, []);
        map.get(did).push(g);
      }
      // Goals carry the BARE repo code ('READ') while apiDomains ids are the
      // 'DOMAIN-{code}' form — resolve labels and ordering through both shapes
      // so a repo-coded goal gets the repo's display name ('Reading'), matching
      // what the IEP form shows for the same goal.
      const nameById = new Map(apiDomains.map((d) => [d.id, d.name]));
      const labelFor = (id) => {
        if (id === UNASSIGNED_DOMAIN_ID) return intl.formatMessage({ id: 'iep.goalDomainUnassigned' });
        return nameById.get(id) || nameById.get(`DOMAIN-${id}`) || domainLabel(id, intl);
      };
      const orderedIds = [];
      const seenIds = new Set();
      for (const d of apiDomains) {
        const bare = d.id.replace(/^DOMAIN-/, '');
        const key = map.has(d.id) ? d.id : map.has(bare) ? bare : null;
        if (key && !seenIds.has(key)) {
          orderedIds.push(key);
          seenIds.add(key);
        }
      }
      for (const id of map.keys()) {
        if (id !== UNASSIGNED_DOMAIN_ID && !seenIds.has(id)) {
          orderedIds.push(id);
          seenIds.add(id);
        }
      }
      if (map.has(UNASSIGNED_DOMAIN_ID)) orderedIds.push(UNASSIGNED_DOMAIN_ID);
      return orderedIds.map((id) => ({ id, label: labelFor(id), goals: map.get(id) }));
    },
    [intl, apiDomains]
  );

  const goalsByDomain = useMemo(() => groupByDomain(visibleGoals), [groupByDomain, visibleGoals]);

  // The outer grouping: SERVICE, each holding its own domain folders. A
  // supervisor reviews one service at a time, so the service is the folder they
  // open and close; the domains live inside it. Goals with no service yet land
  // in a group of their own at the end rather than being hidden.
  const goalsByService = useMemo(() => {
    const map = new Map();
    for (const g of visibleGoals) {
      const code = g.serviceCode || UNASSIGNED_SERVICE_ID;
      if (!map.has(code)) map.set(code, []);
      map.get(code).push(g);
    }
    const orderedCodes = [
      ...GOAL_SERVICES.filter((sv) => map.has(sv.code)).map((sv) => sv.code),
      // A code no longer offered in the service list still has to render.
      ...[...map.keys()].filter((c) => c !== UNASSIGNED_SERVICE_ID && !GOAL_SERVICES.some((sv) => sv.code === c)),
      ...(map.has(UNASSIGNED_SERVICE_ID) ? [UNASSIGNED_SERVICE_ID] : [])
    ];
    return orderedCodes.map((code) => ({
      code,
      label: code === UNASSIGNED_SERVICE_ID ? 'No service set' : serviceLabel(code),
      goals: map.get(code),
      domains: groupByDomain(map.get(code))
    }));
  }, [visibleGoals, groupByDomain]);

  // After an add / target-add + reload, scroll the affected goal into view (and
  // open its domain group) so the user immediately sees what they created — set
  // by the handlers, cleared once the scroll fires. Runs when goalsByDomain
  // changes so it fires after the reload has rendered the new/updated goal.
  const [scrollTargetGoal, setScrollTargetGoal] = useState(null);
  // Goal to flash with a green border right after it's scrolled to (a newly
  // added goal, or a goal that just received a target). Auto-clears below.
  const [highlightGoal, setHighlightGoal] = useState(null);
  useEffect(() => {
    if (!scrollTargetGoal) return undefined;
    const grp = goalsByDomain.find((g) => g.goals.some((x) => x.goalId === scrollTargetGoal));
    if (grp) {
      setExpandedDomains((prev) => {
        if (prev === null || prev.has(grp.id)) return prev;
        const next = new Set(prev);
        next.add(grp.id);
        return next;
      });
    }
    // Two rAFs — wait for the (possibly just-expanded) group to commit + lay out.
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-goal-code="${scrollTargetGoal}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightGoal(scrollTargetGoal);
        setScrollTargetGoal(null);
      })
    );
    return () => cancelAnimationFrame(raf);
  }, [scrollTargetGoal, goalsByDomain]);

  // Clear the green highlight ~4s after it lands.
  useEffect(() => {
    if (!highlightGoal) return undefined;
    const t = setTimeout(() => setHighlightGoal(null), 4000);
    return () => clearTimeout(t);
  }, [highlightGoal]);

  // Build the read-only goal label shown at the top of the target dialog,
  // e.g. "GOAL-001 — CLIENT will tact 10 actions…".
  const buildGoalLabel = (goal) => {
    const text = goal.name || goal.description || '';
    return [goal.goalId, text].filter(Boolean).join(' — ');
  };

  // Add a goal chosen from the Skill Repository builder. The goal is appended to
  // the version SNAPSHOT (the source of truth); reloadSnapshot then self-heals it
  // into a real iep_goal row — which, for a supervisor, lands as "Pending
  // approval" via the backend approval flow.
  const handleAddGoal = async (g) => {
    if (!selectedIepId || !selectedVersionId) return;
    setSaving(true);
    try {
      // Create the goal as a live iep_goal row — the source of truth this tab
      // reads. Child-level adds apply directly (no approval).
      await createGoal(selectedIepId, {
        iep_id: selectedIepId,
        iep_version_id: selectedVersionId,
        goal_code: g.goalId,
        skill_domain_code: g.domainCode || null,
        description: g.statement || g.name,
        baseline: g.baseline || null,
        target_criteria: g.targetCriteria || null,
        start_date: g.startDate || null,
        target_date: g.endDate || null,
        // Without this the goal lands unassigned and never reaches its service's
        // data sheet - the one thing a repository goal cannot work without.
        service_code: g.serviceCode || null,
        status_code: 'active'
      });
      // Mirror into the IEP version SNAPSHOT (best-effort) so the IEP form /
      // document shows the same goal. Live rows stay the source of truth here.
      // The domain is written as the canonical 'DOMAIN-{repo code}' id — the
      // IEP form groups goals and resolves its Skill domain dropdown by that
      // id, so a bare code here would land the goal in the wrong section there.
      try {
        const versionRes = await getIepVersion(selectedVersionId);
        const snap = versionRes?.snapshot_data || {};
        const iepGoals = Array.isArray(snap.iepGoals) ? [...snap.iepGoals] : [];
        const domainId = g.domainCode ? `DOMAIN-${String(g.domainCode).replace(/^DOMAIN-/i, '')}` : '';
        const at = iepGoals.findIndex((x) => x?.goalId === g.goalId);
        if (at === -1) {
          iepGoals.push({
            goalId: g.goalId,
            name: g.statement || g.name,
            description: g.statement || g.name,
            status: g.status || 'in_progress',
            startDate: g.startDate || '',
            endDate: g.endDate || '',
            domain: domainId,
            serviceCode: g.serviceCode || '',
            baseline: g.baseline || '',
            notes: '',
            targets: []
          });
        } else {
          // Stale entry with this code (e.g. left over from an older sync) —
          // realign its domain with the live row we just created.
          iepGoals[at] = { ...iepGoals[at], domain: domainId, serviceCode: g.serviceCode || iepGoals[at].serviceCode || '' };
        }
        await updateIepVersion(selectedVersionId, { snapshot_data: { ...snap, iepGoals } });
      } catch (mirrorErr) {
        console.warn('[GoalsTargetsTab] snapshot mirror failed (non-fatal):', mirrorErr?.message);
      }
      setGoalBuilderOpen(false);
      await reloadSnapshot();
      setScrollTargetGoal(g.goalId);
    } catch (err) {
      console.error('[GoalsTargetsTab] add goal failed:', err);
      setError(err?.response?.data?.detail || err?.message || 'Failed to add goal');
    } finally {
      setSaving(false);
    }
  };

  // Mirror a goal mutation into the IEP version SNAPSHOT (best-effort) so the
  // IEP form / document stays in sync with the live iep_goal rows this tab
  // edits — the same bi-directional pattern the Tracked Behaviors tab uses.
  // `mutate(iepGoals)` returns the next iepGoals array. Live rows remain the
  // source of truth here; a failed mirror never blocks the live write.
  const mirrorGoalsToSnapshot = async (mutate) => {
    try {
      const versionRes = await getIepVersion(selectedVersionId);
      const snap = versionRes?.snapshot_data || {};
      const iepGoals = Array.isArray(snap.iepGoals) ? [...snap.iepGoals] : [];
      const next = mutate(iepGoals);
      await updateIepVersion(selectedVersionId, { snapshot_data: { ...snap, iepGoals: next } });
    } catch (mirrorErr) {
      console.warn('[GoalsTargetsTab] snapshot mirror failed (non-fatal):', mirrorErr?.message);
    }
  };

  // Next free "G-00n" code across every goal currently shown (mirrors
  // GoalBuilderModal.nextCode so custom + repo goals share one sequence).
  const nextGoalCode = () => {
    let max = 0;
    for (const grp of goalsByDomain) {
      for (const g of grp.goals) {
        const m = /(\d+)\s*$/.exec(g.goalId || '');
        if (m) max = Math.max(max, parseInt(m[1], 10));
      }
    }
    return `G-${String(max + 1).padStart(3, '0')}`;
  };

  // Add OR edit a free-form (custom) goal from GoalDetailsModal. Whether the
  // goal already has a live row (by code) decides create vs update, so the same
  // handler serves both the "Add Goal" and pencil-edit entry points.
  const handleSaveCustomGoal = async (data) => {
    if (!selectedIepId || !selectedVersionId) return;
    const existingRowId = goalRowIdByCode[data.goalId];
    setSaving(true);
    try {
      if (existingRowId) {
        // Edit — status is auto-managed here, so it's intentionally not written.
        await updateGoal(selectedIepId, existingRowId, {
          description: data.name,
          goal_description: data.goalDescription || null,
          skill_domain_code: data.domainCode || null,
          service_code: data.serviceCode || null,
          activities: data.activities || [],
          baseline: data.baseline || null,
          start_date: data.startDate || null,
          target_date: data.endDate || null
        });
      } else {
        await createGoal(selectedIepId, {
          iep_id: selectedIepId,
          iep_version_id: selectedVersionId,
          goal_code: data.goalId,
          skill_domain_code: data.domainCode || null,
          service_code: data.serviceCode || null,
          description: data.name,
          goal_description: data.goalDescription || null,
          activities: data.activities || [],
          baseline: data.baseline || null,
          target_criteria: null,
          start_date: data.startDate || null,
          target_date: data.endDate || null,
          status_code: 'active'
        });
      }
      // Keep the snapshot's iepGoals in step (add or patch in place). Domain is
      // written as the canonical 'DOMAIN-{code}' id the IEP form groups by.
      await mirrorGoalsToSnapshot((iepGoals) => {
        const at = iepGoals.findIndex((x) => x?.goalId === data.goalId);
        const base = at === -1 ? {} : iepGoals[at];
        const entry = {
          ...base,
          goalId: data.goalId,
          name: data.name,
          description: data.name,
          goalDescription: data.goalDescription || '',
          serviceCode: data.serviceCode || '',
          activities: data.activities || [],
          status: base.status || 'in_progress',
          startDate: data.startDate || '',
          endDate: data.endDate || '',
          domain: data.domainId || '',
          baseline: data.baseline || '',
          notes: base.notes || '',
          targets: base.targets || []
        };
        if (at === -1) return [...iepGoals, entry];
        const copy = [...iepGoals];
        copy[at] = entry;
        return copy;
      });
      setCustomGoalModal(null);
      await reloadSnapshot();
      setScrollTargetGoal(data.goalId);
    } catch (err) {
      console.error('[GoalsTargetsTab] save custom goal failed:', err);
      setError(err?.response?.data?.detail || err?.message || 'Failed to save goal');
    } finally {
      setSaving(false);
    }
  };

  // Actual goal delete — runs after the styled confirm dialog. Soft-deletes the
  // live row (backend cascades to its targets) and drops it from the snapshot.
  const runDeleteGoal = async (goal) => {
    if (!selectedIepId) return;
    const rowId = goalRowIdByCode[goal.goalId];
    if (!rowId) {
      setError(`No iep_goal row for ${goal.goalId} — try reloading the page.`);
      return;
    }
    setSaving(true);
    try {
      await deleteGoal(selectedIepId, rowId);
      await mirrorGoalsToSnapshot((iepGoals) => iepGoals.filter((g) => g?.goalId !== goal.goalId));
      await reloadSnapshot();
    } catch (err) {
      console.error('[GoalsTargetsTab] delete goal failed:', err);
      setError(err?.response?.data?.detail || err?.message || 'Failed to delete goal');
    } finally {
      setSaving(false);
    }
  };

  // Delete a goal — opens the styled confirm (warns it also removes targets).
  const handleDeleteGoal = (goal) =>
    setConfirmDialog({
      title: intl.formatMessage({ id: 'goalsTargets.deleteGoalDialogTitle' }),
      message: intl.formatMessage({ id: 'goalsTargets.deleteGoalMessage' }, { name: goal.name || goal.description || goal.goalId }),
      confirmLabel: intl.formatMessage({ id: 'goalsTargets.delete' }),
      confirmColor: 'error',
      action: () => runDeleteGoal(goal)
    });

  // Add one or more targets chosen from the domain's repository Target Library.
  const handleAddTargets = async (chosen) => {
    const tp = targetPicker;
    if (!tp || !selectedIepId) {
      setTargetPicker(null);
      return;
    }
    const goalRowId = goalRowIdByCode[tp.goalCode];
    if (!goalRowId) {
      setError(`No iep_goal row for ${tp.goalCode} — try reloading the page.`);
      setTargetPicker(null);
      return;
    }
    setSaving(true);
    setTargetPicker(null);
    try {
      // New targets take the child's (else the global) Mastery Prompt Level.
      const masteryState = childId ? await getChildMasteryRules(childId).catch(() => null) : null;
      const masteryPromptLevel = resolveDefaultMasteryLevel(masteryState, goalRowId);
      for (const t of chosen) {
        const payload = draftToTargetPayload({
          name: t.name,
          nameTemplate: t.nameTemplate,
          materials: t.materials,
          parameters: t.parameters,
          masteryPromptLevel,
          status: 'in_progress',
          // Supervisor-added targets are approved on create (mirrors handleOpenAdd).
          approvedById: supervisorPersonId || '',
          approvedByName: supervisorName,
          approvedDate: todayIso()
        });
        const row = await createTarget(selectedIepId, goalRowId, payload);
        const next = backendTargetToDraft(row);
        setOverlay((prev) => {
          const cur = prev[tp.goalCode] || [];
          return { ...prev, [tp.goalCode]: cur.some((x) => x.id === next.id) ? cur : [...cur, next] };
        });
      }
      reloadSnapshot();
      setScrollTargetGoal(tp.goalCode);
    } catch (err) {
      console.error('[GoalsTargetsTab] add targets failed:', err);
      setError(err?.response?.data?.detail || err?.message || 'Failed to add targets');
    } finally {
      setSaving(false);
    }
  };

  // Targets are added via the repository Target Picker (setTargetPicker); the
  // free-form TargetDialog is now used only for editing an existing target.
  const handleOpenEdit = (goal, target) => {
    setDialogGoalId(goal.goalId);
    setDialogGoalDomainId(goal.domain || '');
    setDialogGoalLabel(buildGoalLabel(goal));
    setDialogInitial(target);
    setDialogOpen(true);
  };

  // Save handler — `saved` is the draft from TargetDialog.
  //   • dialogInitial set       → PUT  /iep/{iep}/goals/{goal}/targets/{id}
  //   • dialogInitial null + draft.id missing UUID → POST (new row)
  //   • dialogInitial null + draft.id IS UUID      → also POST (shouldn't happen)
  const handleDialogSave = async (saved) => {
    if (!selectedIepId || !dialogGoalId) {
      setDialogOpen(false);
      return;
    }
    const goalRowId = goalRowIdByCode[dialogGoalId];
    if (!goalRowId) {
      setError(`No iep_goal row for ${dialogGoalId} — try reloading the page.`);
      setDialogOpen(false);
      return;
    }
    const payload = draftToTargetPayload(saved);
    setSaving(true);
    setDialogOpen(false);
    try {
      let row;
      if (dialogInitial?.id) {
        row = await updateTarget(selectedIepId, goalRowId, dialogInitial.id, payload);
      } else {
        row = await createTarget(selectedIepId, goalRowId, payload);
      }
      const next = backendTargetToDraft(row);
      setOverlay((prev) => {
        const current = prev[dialogGoalId] || [];
        const exists = current.some((t) => t.id === next.id);
        const updatedList = exists ? current.map((t) => (t.id === next.id ? next : t)) : [...current, next];
        return { ...prev, [dialogGoalId]: updatedList };
      });
      // Re-run goal-completion check: if this save mastered the last target,
      // reloadSnapshot will auto-complete the goal and persist the status update.
      reloadSnapshot();
      setScrollTargetGoal(dialogGoalId);
    } catch (err) {
      console.error('[GoalsTargetsTab] save failed:', err);
      setError(err?.response?.data?.detail || err?.message || 'Failed to save target');
    } finally {
      setSaving(false);
    }
  };

  // Approve a pending (therapist-added) target: stamp the current supervisor
  // as approver + today's date. After this the target is "approved" and
  // becomes usable in data collection. goalCode is the goal's code (overlay key).
  const handleApproveTarget = async (goalCode, target) => {
    if (!selectedIepId) return;
    const goalRowId = goalRowIdByCode[goalCode];
    if (!goalRowId) {
      setError(`No iep_goal row for ${goalCode} — try reloading the page.`);
      return;
    }
    setSaving(true);
    try {
      const row = await updateTarget(selectedIepId, goalRowId, target.id, {
        approved_by_id: supervisorPersonId || null,
        approved_by_name: supervisorName,
        approved_date: todayIso()
      });
      const next = backendTargetToDraft(row);
      setOverlay((prev) => ({
        ...prev,
        [goalCode]: (prev[goalCode] || []).map((t) => (t.id === next.id ? next : t))
      }));
    } catch (err) {
      console.error('[GoalsTargetsTab] approve failed:', err);
      setError(err?.response?.data?.detail || err?.message || 'Failed to approve target');
    } finally {
      setSaving(false);
    }
  };

  // NOTE: Goals added to a CHILD's IEP by a supervisor go in directly — no
  // approval is required (the skill-repo admin-approval flow is separate). Only
  // therapist-added TARGETS (from a session) need supervisor approval; that flow
  // lives in handleApproveTarget / runDeclineTarget below.

  // Actual decline — marks the target as declined (kept for tracking, shown
  // struck-through, and excluded from data collection). Stamps the current
  // supervisor + today's date. Runs after the styled confirm dialog.
  const runDeclineTarget = async (goalCode, target) => {
    if (!selectedIepId) return;
    const goalRowId = goalRowIdByCode[goalCode];
    if (!goalRowId) {
      setError(`No iep_goal row for ${goalCode} — try reloading the page.`);
      return;
    }
    setSaving(true);
    try {
      const row = await updateTarget(selectedIepId, goalRowId, target.id, {
        declined_by_id: supervisorPersonId || null,
        declined_by_name: supervisorName,
        declined_date: todayIso()
      });
      const next = backendTargetToDraft(row);
      setOverlay((prev) => ({
        ...prev,
        [goalCode]: (prev[goalCode] || []).map((t) => (t.id === next.id ? next : t))
      }));
      closeTargetDetail();
    } catch (err) {
      console.error('[GoalsTargetsTab] decline failed:', err);
      setError(err?.response?.data?.detail || err?.message || 'Failed to decline target');
    } finally {
      setSaving(false);
    }
  };

  // Decline a pending (therapist-added) target — opens the styled confirm.
  const handleDeclineTarget = (goalCode, target) =>
    setConfirmDialog({
      title: intl.formatMessage({ id: 'goalsTargets.declineTargetTitle' }),
      message: intl.formatMessage(
        { id: 'goalsTargets.declineTargetConfirm' },
        { name: target.name || intl.formatMessage({ id: 'goalsTargets.tableTarget' }) }
      ),
      confirmLabel: intl.formatMessage({ id: 'goalsTargets.decline' }),
      confirmColor: 'error',
      action: () => runDeclineTarget(goalCode, target)
    });

  // Actual delete — runs after the styled confirm dialog.
  const runDelete = async (goalCode, targetId) => {
    if (!selectedIepId) return;
    const goalRowId = goalRowIdByCode[goalCode];
    if (!goalRowId) {
      setError(`No iep_goal row for ${goalCode} — try reloading the page.`);
      return;
    }
    setSaving(true);
    try {
      await deleteTarget(selectedIepId, goalRowId, targetId);
      setOverlay((prev) => ({
        ...prev,
        [goalCode]: (prev[goalCode] || []).filter((t) => t.id !== targetId)
      }));
    } catch (err) {
      console.error('[GoalsTargetsTab] delete failed:', err);
      setError(err?.response?.data?.detail || err?.message || 'Failed to delete target');
    } finally {
      setSaving(false);
    }
  };

  // Delete a target — opens the styled confirm.
  const handleDelete = (goalCode, targetId, targetName) =>
    setConfirmDialog({
      title: intl.formatMessage({ id: 'goalsTargets.deleteTargetDialogTitle' }),
      message: intl.formatMessage(
        { id: 'goalsTargets.deleteTargetMessage' },
        { name: targetName || intl.formatMessage({ id: 'goalsTargets.tableTarget' }) }
      ),
      confirmLabel: intl.formatMessage({ id: 'goalsTargets.delete' }),
      confirmColor: 'error',
      action: () => runDelete(goalCode, targetId)
    });

  // Derived expand/collapse button states.
  // expandedDomains === null means "all expanded" (the initial default).
  const allDomainIds = goalsByDomain.map((g) => g.id);
  const allServiceCodes = goalsByService.map((g) => g.code);
  const allExpanded =
    (expandedDomains === null || allDomainIds.every((id) => expandedDomains.has(id))) &&
    (expandedServices === null || allServiceCodes.every((c) => expandedServices.has(c)));
  const allCollapsed = expandedDomains !== null && expandedDomains.size === 0 && expandedServices !== null && expandedServices.size === 0;

  // Expand / collapse act on BOTH levels: collapsing only the domains would
  // leave the service folders open around empty space, which does not read as
  // "collapsed".
  const expandAll = () => {
    setExpandedDomains(new Set(allDomainIds));
    setExpandedServices(new Set(allServiceCodes));
  };
  const collapseAll = () => {
    setExpandedDomains(new Set());
    setExpandedServices(new Set());
  };

  if (loadingVersions) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (versions.length === 0) {
    return (
      <MainCard>
        <Stack alignItems="center" spacing={1.5} sx={{ py: 4 }}>
          <AimOutlined style={{ fontSize: '3rem', color: theme.palette.grey[300] }} />
          <Typography variant="h6" color="text.secondary">
            {intl.formatMessage({ id: 'goalsTargets.noIepYet' })}
          </Typography>
          <Typography variant="body2" color="text.disabled">
            {intl.formatMessage({ id: 'goalsTargets.noIepHint' })}
          </Typography>
        </Stack>
      </MainCard>
    );
  }

  return (
    <Stack spacing={3}>
      {error && (
        <Alert severity="warning" variant="outlined" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header: version dropdown + summary chip */}
      <MainCard>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <AimOutlined style={{ fontSize: '1.4rem', color: theme.palette.primary.main }} />
            <Typography variant="h4">{intl.formatMessage({ id: 'goalsTargets.goalsAndTargets' })}</Typography>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <TextField
              select
              size="small"
              label="Service"
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All services</MenuItem>
              {GOAL_SERVICES.map((sv) => (
                <MenuItem key={sv.code} value={sv.code}>
                  {sv.name}
                </MenuItem>
              ))}
              <MenuItem value="none">No service set</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label={intl.formatMessage({ id: 'goalsTargets.iepVersion' })}
              value={selectedVersionId}
              onChange={(e) => setSelectedVersionId(e.target.value)}
              sx={{ minWidth: 260 }}
            >
              {versions.map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {v.version_label || `Version ${v.version_number}`}
                  {v.iep_start_date
                    ? ` — ${intl.formatDate(new Date(v.iep_start_date), { month: 'short', day: '2-digit', year: 'numeric' })}`
                    : ''}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>

        {snapshot && (
          <Stack spacing={1} sx={{ mt: 2 }}>
            {/* Child name */}
            {snapshot.childName && (
              <Stack direction="row">
                <Chip size="small" label={intl.formatMessage({ id: 'goalsTargets.childChip' }, { name: snapshot.childName })} />
              </Stack>
            )}

            {/* Metadata chips (left) + global expand / collapse (right) */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {snapshot.childId && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={intl.formatMessage({ id: 'goalsTargets.idChip' }, { id: snapshot.childId })}
                  />
                )}
                {snapshot.startDate && snapshot.endDate && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={intl.formatMessage({ id: 'goalsTargets.iepWindowChip' }, { start: snapshot.startDate, end: snapshot.endDate })}
                  />
                )}
                <Chip
                  size="small"
                  color="primary"
                  label={intl.formatMessage(
                    { id: 'goalsTargets.goalsCountPlural' },
                    { count: goalsByDomain.reduce((n, d) => n + d.goals.length, 0) }
                  )}
                />
              </Stack>

              {/* Add goal (repository builder) + expand / collapse all */}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
                {/* Shown but greyed out under Full-Read — the viewer can see the action
                    exists but cannot add goals. Enabled only with Full access.
                    Two entry points, mirroring the IEP form: a free-form custom
                    goal, and a goal built from the Skill Repository. */}
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PlusOutlined style={{ fontSize: '0.7rem' }} />}
                  onClick={() => setCustomGoalModal({ initial: null })}
                  disabled={readOnly}
                >
                  {intl.formatMessage({ id: 'goalsTargets.addGoal' })}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PlusOutlined style={{ fontSize: '0.7rem' }} />}
                  onClick={() => setGoalBuilderOpen(true)}
                  disabled={readOnly}
                >
                  {intl.formatMessage({ id: 'goalsTargets.addFromSkillRepo' })}
                </Button>
                {goalsByDomain.length > 0 && (
                  <>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownOutlined style={{ fontSize: '0.7rem' }} />}
                      disabled={allExpanded}
                      onClick={expandAll}
                    >
                      {intl.formatMessage({ id: 'goalsTargets.expandAll' })}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<UpOutlined style={{ fontSize: '0.7rem' }} />}
                      disabled={allCollapsed}
                      onClick={collapseAll}
                    >
                      {intl.formatMessage({ id: 'goalsTargets.collapseAll' })}
                    </Button>
                  </>
                )}
              </Stack>
            </Stack>
          </Stack>
        )}
      </MainCard>

      {/* Goals grouped by domain */}
      {loadingSnapshot ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress />
        </Stack>
      ) : goalsByDomain.length === 0 ? (
        <MainCard>
          <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
            <FolderOpenOutlined style={{ fontSize: '2.5rem', color: theme.palette.grey[300] }} />
            {/* An empty list because of the filter is not the same as an empty
                version — say which, or the supervisor reads "no goals yet" on a
                child who has fifteen. */}
            <Typography variant="body1" color="text.secondary">
              {serviceFilter && liveGoals.length > 0
                ? `No goals under ${serviceFilter === 'none' ? 'this filter' : serviceLabel(serviceFilter)}.`
                : intl.formatMessage({ id: 'goalsTargets.noGoalsYet' })}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {serviceFilter && liveGoals.length > 0
                ? 'Clear the Service filter to see the rest of this version.'
                : intl.formatMessage({ id: 'goalsTargets.noGoalsHint' })}
            </Typography>
          </Stack>
        </MainCard>
      ) : (
        goalsByService.map((service) => (
          <Accordion
            key={service.code}
            expanded={expandedServices === null ? true : expandedServices.has(service.code)}
            onChange={(_, isExpanded) => {
              setExpandedServices((prev) => {
                const base = prev === null ? new Set(goalsByService.map((x) => x.code)) : new Set(prev);
                if (isExpanded) base.add(service.code);
                else base.delete(service.code);
                return base;
              });
            }}
            disableGutters
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              '&:before': { display: 'none' },
              bgcolor: 'background.paper'
            }}
          >
            <AccordionSummary
              expandIcon={<DownOutlined style={{ fontSize: '0.75rem' }} />}
              sx={{
                bgcolor: alpha(service.code === UNASSIGNED_SERVICE_ID ? theme.palette.warning.main : theme.palette.info.main, 0.08),
                minHeight: 56
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1 }}>
                <Typography variant="h5">{service.label}</Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  color={service.code === UNASSIGNED_SERVICE_ID ? 'warning' : 'info'}
                  label={intl.formatMessage({ id: 'goalsTargets.goalsCountPlural' }, { count: service.goals.length })}
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <Stack spacing={2}>
                {service.domains.map((group) => {
                  const goalCount = group.goals.length;
                  const targetCount = group.goals.reduce((n, g) => n + (overlay[g.goalId] ? overlay[g.goalId].length : 0), 0);
                  return (
                    <Accordion
                      key={group.id}
                      expanded={expandedDomains === null ? true : expandedDomains.has(group.id)}
                      onChange={(_, isExpanded) => {
                        setExpandedDomains((prev) => {
                          const base = prev === null ? new Set(goalsByDomain.map((g) => g.id)) : new Set(prev);
                          if (isExpanded) base.add(group.id);
                          else base.delete(group.id);
                          return base;
                        });
                      }}
                      disableGutters
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        '&:before': { display: 'none' },
                        bgcolor: 'background.paper'
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<DownOutlined style={{ fontSize: '0.75rem' }} />}
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          minHeight: 56
                        }}
                      >
                        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1 }}>
                          <FolderOpenOutlined
                            style={{
                              fontSize: '1.1rem',
                              color: group.id === UNASSIGNED_DOMAIN_ID ? theme.palette.warning.main : theme.palette.primary.main
                            }}
                          />
                          <Typography variant="subtitle1" fontWeight={700}>
                            {group.label}
                          </Typography>
                          <Chip
                            size="small"
                            label={intl.formatMessage({ id: 'goalsTargets.goalsCountPlural' }, { count: goalCount })}
                            color={group.id === UNASSIGNED_DOMAIN_ID ? 'warning' : 'primary'}
                            variant="outlined"
                          />
                          {targetCount > 0 && (
                            <Chip size="small" label={intl.formatMessage({ id: 'goalsTargets.targetCount' }, { count: targetCount })} />
                          )}
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 2 }}>
                        <Stack spacing={2}>
                          {group.goals.map((goal) => {
                            const targets = overlay[goal.goalId] || [];
                            // A speech / OT goal is worked through session activities, not
                            // through targets. Its activities are authored in the Edit goal
                            // dialog (the pencil), which is the one place they are edited -
                            // here the card only shows them.
                            const usesActivities = serviceHasActivities(goal.serviceCode);
                            const goalActivities = activityLines(goal.activities);
                            return (
                              <Paper
                                key={goal.goalId}
                                data-goal-code={goal.goalId}
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  transition: (t) => t.transitions.create(['border-color', 'box-shadow']),
                                  ...(highlightGoal === goal.goalId && {
                                    borderColor: 'success.main',
                                    boxShadow: (t) => `0 0 0 2px ${alpha(t.palette.success.main, 0.45)}`
                                  })
                                }}
                              >
                                {/* Goal header */}
                                <Stack
                                  direction={{ xs: 'column', sm: 'row' }}
                                  spacing={1}
                                  justifyContent="space-between"
                                  alignItems="flex-start"
                                  sx={{ mb: 1 }}
                                >
                                  <Stack
                                    direction="row"
                                    spacing={1.25}
                                    alignItems="center"
                                    flexWrap="wrap"
                                    useFlexGap
                                    // flex:1 + minWidth:0 lets the goal description wrap
                                    // onto a new line BEFORE colliding with the button,
                                    // instead of squeezing the button's label across 2 lines.
                                    sx={{ flex: 1, minWidth: 0 }}
                                  >
                                    <Typography variant="subtitle2" color="primary.main" fontWeight={700}>
                                      {goal.goalId || '—'}
                                    </Typography>
                                    <Typography variant="subtitle1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                                      {goal.name || goal.description || intl.formatMessage({ id: 'goalsTargets.unnamedGoal' })}
                                    </Typography>
                                    {goal.status && (
                                      <Chip
                                        size="small"
                                        label={
                                          GOAL_STATUS_INTL_KEYS[goal.status]
                                            ? intl.formatMessage({ id: GOAL_STATUS_INTL_KEYS[goal.status] })
                                            : String(goal.status).replace(/_/g, ' ')
                                        }
                                        color={goal.status === GOAL_SNAPSHOT_STATUS_COMPLETED ? 'success' : 'default'}
                                      />
                                    )}
                                  </Stack>
                                  {!readOnly && (
                                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                                      {/* Targets are an ABA construct - an acquisition ladder
                                          scored trial by trial. A speech / OT goal has
                                          activities instead, added through the pencil, so it
                                          is offered no Add Target at all rather than a button
                                          that would file the wrong kind of child under it. */}
                                      {!usesActivities && (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          startIcon={<PlusOutlined />}
                                          disabled={saving}
                                          onClick={() =>
                                            setTargetPicker({ goalCode: goal.goalId, domainCode: goal.domain, domainName: group.label })
                                          }
                                          sx={{ whiteSpace: 'nowrap' }}
                                        >
                                          {intl.formatMessage({ id: 'goalsTargets.addTarget' })}
                                        </Button>
                                      )}
                                      <Tooltip title={intl.formatMessage({ id: 'goalsTargets.editGoal' })}>
                                        <span>
                                          <IconButton size="small" disabled={saving} onClick={() => setCustomGoalModal({ initial: goal })}>
                                            <EditOutlined />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip title={intl.formatMessage({ id: 'goalsTargets.delete' })}>
                                        <span>
                                          <IconButton size="small" color="error" disabled={saving} onClick={() => handleDeleteGoal(goal)}>
                                            <DeleteOutlined />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </Stack>
                                  )}
                                </Stack>

                                {/* The narrative the author wrote under the statement. */}
                                {goal.goalDescription && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, wordBreak: 'break-word' }}>
                                    {goal.goalDescription}
                                  </Typography>
                                )}

                                {/* Session activities (speech / OT goals) */}
                                {goalActivities.length > 0 && (
                                  <Box component="ol" sx={{ m: 0, mb: 1.5, pl: 3 }}>
                                    {goalActivities.map((act, i) => (
                                      <Box component="li" key={i}>
                                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                          {act}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                )}

                                {/* Goal meta line */}
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                                  {goal.serviceCode && (
                                    <Chip size="small" color="info" variant="outlined" label={serviceLabel(goal.serviceCode)} />
                                  )}
                                  {goal.startDate && (
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={intl.formatMessage({ id: 'goalsTargets.startChip' }, { date: goal.startDate })}
                                    />
                                  )}
                                  {goal.endDate && (
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={intl.formatMessage({ id: 'goalsTargets.endChip' }, { date: goal.endDate })}
                                    />
                                  )}
                                  {goal.baseline && (
                                    <Tooltip title={goal.baseline} placement="top">
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={intl.formatMessage({ id: 'goalsTargets.status.baseline' })}
                                      />
                                    </Tooltip>
                                  )}
                                </Stack>

                                {/* Targets - ABA goals only. A target table on a therapy goal
                                    would invite a clinician to run an ABA mastery flow
                                    (STATUS / INTRODUCED / MASTERED) on a goal their service
                                    does not score that way; its activities above take that
                                    place, and the pencil is where they are edited. */}
                                {usesActivities ? (
                                  goalActivities.length === 0 && (
                                    <Typography variant="body2" color="text.disabled" sx={{ py: 1 }}>
                                      {intl.formatMessage({
                                        id: 'goalsTargets.noActivitiesHint',
                                        defaultMessage: 'No activities yet — edit the goal to add them.'
                                      })}
                                    </Typography>
                                  )
                                ) : targets.length === 0 ? (
                                  <Typography variant="body2" color="text.disabled" sx={{ py: 1 }}>
                                    {intl.formatMessage({ id: 'goalsTargets.noTargetsHint' })}
                                  </Typography>
                                ) : (
                                  <TableContainer sx={{ overflowX: 'auto' }}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                                          <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>
                                            {intl.formatMessage({ id: 'goalsTargets.tableTarget' })}
                                          </TableCell>
                                          <TableCell sx={{ fontWeight: 600, width: 110 }}>
                                            {intl.formatMessage({ id: 'goalsTargets.statusHeader' })}
                                          </TableCell>
                                          <TableCell sx={{ fontWeight: 600, width: 120 }}>
                                            {intl.formatMessage({ id: 'goalsTargets.tableIntroduced' })}
                                          </TableCell>
                                          <TableCell sx={{ fontWeight: 600, width: 120 }}>
                                            {intl.formatMessage({ id: 'goalsTargets.tableMastered' })}
                                          </TableCell>
                                          <TableCell sx={{ fontWeight: 600, width: 90 }} align="center">
                                            {intl.formatMessage({ id: 'goalsTargets.tableDays' })}
                                          </TableCell>
                                          <TableCell sx={{ fontWeight: 600, width: 90 }} align="right">
                                            {intl.formatMessage({ id: 'goalsTargets.tableActions' })}
                                          </TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {targets.map((t) => {
                                          const skill = t.skillId ? skillById.get(t.skillId) || null : null;
                                          const tDays = computeDays(t);
                                          return (
                                            <TableRow key={t.id} hover sx={{ cursor: 'pointer' }} onClick={() => openTargetDetail(goal, t)}>
                                              <TableCell>
                                                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                                  <Typography
                                                    variant="body2"
                                                    fontWeight={600}
                                                    sx={
                                                      t.declinedDate
                                                        ? { textDecoration: 'line-through', color: 'text.disabled' }
                                                        : undefined
                                                    }
                                                  >
                                                    {t.name}
                                                  </Typography>
                                                  {/* Declined = supervisor rejected it; kept struck-through for tracking */}
                                                  {readOnly ? null : t.declinedDate ? (
                                                    <Chip
                                                      size="small"
                                                      color="error"
                                                      variant="outlined"
                                                      label={intl.formatMessage({ id: 'goalsTargets.declined' })}
                                                      sx={{ height: 20, fontSize: '0.65rem' }}
                                                    />
                                                  ) : (
                                                    /* Pending = no approved date (a therapist added it; awaiting sign-off) */
                                                    !t.approvedDate && (
                                                      <Chip
                                                        size="small"
                                                        color="warning"
                                                        variant="outlined"
                                                        label={intl.formatMessage({ id: 'goalsTargets.pendingApproval' })}
                                                        sx={{ height: 20, fontSize: '0.65rem' }}
                                                      />
                                                    )
                                                  )}
                                                </Stack>
                                                {skill && (
                                                  <Typography variant="caption" color="text.secondary">
                                                    {skill.code} — {skill.name}
                                                  </Typography>
                                                )}
                                              </TableCell>
                                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                  {t.status && (
                                                    <Chip
                                                      size="small"
                                                      label={
                                                        TARGET_STATUS_INTL_KEYS[t.status]
                                                          ? intl.formatMessage({ id: TARGET_STATUS_INTL_KEYS[t.status] })
                                                          : t.status.replace(/_/g, ' ')
                                                      }
                                                      color="primary"
                                                      variant="outlined"
                                                    />
                                                  )}
                                                  {/* Mastery status change history (rule engine vs person, and why). */}
                                                  <TargetStatusHistoryButton targetId={t.id} targetName={t.name} />
                                                </Stack>
                                              </TableCell>
                                              <TableCell>
                                                <Typography variant="body2">{t.dateIntroduced || '—'}</Typography>
                                              </TableCell>
                                              <TableCell>
                                                <Typography variant="body2">{t.dateMastered || '—'}</Typography>
                                              </TableCell>
                                              <TableCell align="center">
                                                {tDays !== null ? (
                                                  <Chip
                                                    size="small"
                                                    color={t.dateMastered ? 'success' : 'default'}
                                                    label={intl.formatMessage({ id: 'goalsTargets.daysCount' }, { days: tDays })}
                                                  />
                                                ) : (
                                                  <Typography variant="body2" color="text.disabled">
                                                    —
                                                  </Typography>
                                                )}
                                              </TableCell>
                                              <TableCell align="right" onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: 'nowrap' }}>
                                                {!readOnly && !t.approvedDate && !t.declinedDate && (
                                                  <Tooltip title={intl.formatMessage({ id: 'goalsTargets.reviewApprove' })}>
                                                    <IconButton
                                                      size="small"
                                                      color="success"
                                                      disabled={saving}
                                                      onClick={() => openTargetDetail(goal, t)}
                                                    >
                                                      <CheckCircleOutlined />
                                                    </IconButton>
                                                  </Tooltip>
                                                )}
                                                {!readOnly && (
                                                  <>
                                                    <Tooltip title={intl.formatMessage({ id: 'goalsTargets.edit' })}>
                                                      <IconButton size="small" disabled={saving} onClick={() => handleOpenEdit(goal, t)}>
                                                        <EditOutlined />
                                                      </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={intl.formatMessage({ id: 'goalsTargets.delete' })}>
                                                      <IconButton
                                                        size="small"
                                                        color="error"
                                                        disabled={saving}
                                                        onClick={() => handleDelete(goal.goalId, t.id, t.name)}
                                                      >
                                                        <DeleteOutlined />
                                                      </IconButton>
                                                    </Tooltip>
                                                  </>
                                                )}
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                )}
                              </Paper>
                            );
                          })}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))
      )}

      <TargetDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleDialogSave}
        initial={dialogInitial}
        goalDomainId={dialogGoalDomainId}
        goalLabel={dialogGoalLabel}
        childId={childId}
        skills={skills}
      />

      {/* Free-form custom goal add/edit — shared with the IEP form */}
      <GoalDetailsModal
        open={Boolean(customGoalModal)}
        onClose={() => setCustomGoalModal(null)}
        onSave={handleSaveCustomGoal}
        initial={customGoalModal?.initial || null}
        domainOptions={apiDomains}
        statusOptions={[]}
        nextCode={customGoalModal?.initial ? '' : nextGoalCode()}
      />

      {/* Repository-driven Goal Builder (search repo → configure parameter ladders) */}
      <GoalBuilderModal
        open={goalBuilderOpen}
        onClose={() => setGoalBuilderOpen(false)}
        onAdd={handleAddGoal}
        existingCodes={goalsByDomain.flatMap((g) => g.goals.map((x) => x.goalId))}
      />

      {/* Repository Target Library picker */}
      <TargetPickerModal
        open={Boolean(targetPicker)}
        onClose={() => setTargetPicker(null)}
        onAdd={handleAddTargets}
        domainCode={targetPicker?.domainCode}
        domainName={targetPicker?.domainName}
      />

      {/* Target detail dialog — opens when the supervisor clicks a target row */}
      <Dialog open={Boolean(detailTarget)} onClose={closeTargetDetail} maxWidth="md" fullWidth>
        {detailTarget &&
          (() => {
            const t = detailTarget.target;
            const skill = t.skillId ? skillById.get(t.skillId) || null : null;
            const tDays = computeDays(t);
            return (
              <>
                <DialogTitle>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                    <Typography variant="h5" fontWeight={600}>
                      {t.name}
                    </Typography>
                    {t.status && (
                      <Chip
                        size="small"
                        label={
                          TARGET_STATUS_INTL_KEYS[t.status]
                            ? intl.formatMessage({ id: TARGET_STATUS_INTL_KEYS[t.status] })
                            : t.status.replace(/_/g, ' ')
                        }
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {skill && <Chip size="small" label={`${skill.code} — ${skill.name}`} variant="outlined" />}
                  </Stack>
                </DialogTitle>
                <DialogContent dividers>
                  <Stack spacing={2}>
                    {/* Dates + days summary */}
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {t.dateIntroduced && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={intl.formatMessage({ id: 'goalsTargets.introducedChip' }, { date: t.dateIntroduced })}
                        />
                      )}
                      {t.dateMastered && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={intl.formatMessage({ id: 'goalsTargets.masteredChip' }, { date: t.dateMastered })}
                        />
                      )}
                      {tDays !== null && (
                        <Chip
                          size="small"
                          color={t.dateMastered ? 'success' : 'default'}
                          label={intl.formatMessage(
                            { id: t.dateMastered ? 'goalsTargets.daysToMastery' : 'goalsTargets.daysRunning' },
                            { days: tDays }
                          )}
                        />
                      )}
                    </Stack>

                    {/* Approval status — pending targets are reviewed here */}
                    {!t.approvedDate ? (
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label={intl.formatMessage({ id: 'goalsTargets.pendingApprovalFull' })}
                        sx={{ alignSelf: 'flex-start', height: 'auto', py: 0.5, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                      />
                    ) : (
                      <Typography variant="body2" color="success.main">
                        {intl.formatMessage({ id: 'goalsTargets.approved' })}
                        {t.approvedByName ? ` by ${t.approvedByName}` : ''}
                        {t.approvedDate ? ` · ${t.approvedDate}` : ''}
                      </Typography>
                    )}

                    {/* Description */}
                    {t.description && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {intl.formatMessage({ id: 'goalsTargets.description' })}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {t.description}
                        </Typography>
                      </Box>
                    )}

                    {/* Context grid: location / people / stimuli */}
                    {(t.location || t.people || t.stimuli) && (
                      <Grid container spacing={2}>
                        {t.location && (
                          <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              {intl.formatMessage({ id: 'goalsTargets.location' })}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {t.location}
                            </Typography>
                          </Grid>
                        )}
                        {t.people && (
                          <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              {intl.formatMessage({ id: 'goalsTargets.people' })}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {t.people}
                            </Typography>
                          </Grid>
                        )}
                        {t.stimuli && (
                          <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              {intl.formatMessage({ id: 'goalsTargets.stimuli' })}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {t.stimuli}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    )}

                    {/* Materials with thumbnails — bigger than the avatar group in the row */}
                    {Array.isArray(t.materials) && t.materials.length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {intl.formatMessage({ id: 'goalsTargets.materials' })}
                        </Typography>
                        <Box
                          sx={{
                            mt: 1,
                            display: 'grid',
                            gridTemplateColumns: {
                              xs: 'repeat(auto-fill, minmax(96px, 1fr))',
                              sm: 'repeat(auto-fill, minmax(120px, 1fr))'
                            },
                            gap: 1
                          }}
                        >
                          {t.materials.map((m) => (
                            <Box
                              key={`${m.source}-${m.path}`}
                              sx={{
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                                overflow: 'hidden',
                                bgcolor: 'background.paper'
                              }}
                            >
                              <Box
                                component="img"
                                src={materialThumbnailUrl(m)}
                                alt={m.name}
                                sx={{
                                  width: '100%',
                                  aspectRatio: '1 / 1',
                                  objectFit: 'cover',
                                  display: 'block'
                                }}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                              <Typography variant="caption" sx={{ display: 'block', px: 0.75, py: 0.5 }} noWrap title={m.name}>
                                {m.name}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Stack>
                </DialogContent>
                <DialogActions>
                  <Button onClick={closeTargetDetail}>{intl.formatMessage({ id: 'goalsTargets.close' })}</Button>
                  {t.declinedDate ? (
                    // Already declined → show who/when, allow Edit only.
                    <>
                      <Typography variant="caption" color="error.main" sx={{ mr: 'auto', pl: 1 }}>
                        {intl.formatMessage({ id: 'goalsTargets.declined' })}
                        {t.declinedByName ? ` by ${t.declinedByName}` : ''} · {t.declinedDate}
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<EditOutlined />}
                        onClick={() => {
                          closeTargetDetail();
                          handleOpenEdit(detailTarget.goal, t);
                        }}
                      >
                        {intl.formatMessage({ id: 'goalsTargets.edit' })}
                      </Button>
                    </>
                  ) : !t.approvedDate ? (
                    <>
                      {/* Pending target → supervisor reviews and decides. Decline
                          marks it (kept, struck-through); Approve stamps approver + today. */}
                      <Button
                        color="error"
                        variant="outlined"
                        disabled={saving}
                        onClick={() => handleDeclineTarget(detailTarget.goal.goalId, t)}
                      >
                        {intl.formatMessage({ id: 'goalsTargets.decline' })}
                      </Button>
                      <Button
                        color="success"
                        variant="contained"
                        startIcon={<CheckCircleOutlined />}
                        disabled={saving}
                        onClick={() => {
                          handleApproveTarget(detailTarget.goal.goalId, t);
                          closeTargetDetail();
                        }}
                      >
                        {intl.formatMessage({ id: 'goalsTargets.approve' })}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={<EditOutlined />}
                      onClick={() => {
                        closeTargetDetail();
                        handleOpenEdit(detailTarget.goal, t);
                      }}
                    >
                      {intl.formatMessage({ id: 'goalsTargets.edit' })}
                    </Button>
                  )}
                </DialogActions>
              </>
            );
          })()}
      </Dialog>

      {/* Styled confirmation (replaces native window.confirm) for delete/decline */}
      <Dialog open={Boolean(confirmDialog)} onClose={() => !saving && setConfirmDialog(null)} maxWidth="xs" fullWidth>
        {confirmDialog && (
          <>
            <DialogTitle>{confirmDialog.title}</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {confirmDialog.message}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmDialog(null)} disabled={saving}>
                {intl.formatMessage({ id: 'goalsTargets.cancel' })}
              </Button>
              <Button
                color={confirmDialog.confirmColor || 'primary'}
                variant="contained"
                disabled={saving}
                onClick={() => {
                  const act = confirmDialog.action;
                  setConfirmDialog(null);
                  if (act) act();
                }}
              >
                {confirmDialog.confirmLabel || intl.formatMessage({ id: 'goalsTargets.confirm' })}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Stack>
  );
};

GoalsTargetsTab.propTypes = {
  childId: PropTypes.string,
  initialIEPVersionId: PropTypes.string,
  readOnly: PropTypes.bool,
  // childInfo is accepted for backward compat with old callers but unused
  // in the new implementation (we read child meta from the version snapshot).
  childInfo: PropTypes.object
};

export default GoalsTargetsTab;
